import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Row, Col, Button, message, Table, Tag, Modal, Form, Input, Select,
  DatePicker, Space, Statistic, Tabs, InputNumber, Popconfirm, Timeline, Typography, Empty, Switch, Segmented, Collapse,
  Checkbox,
} from 'antd';
import {
  UserOutlined, PhoneOutlined, WechatOutlined, EnvironmentOutlined,
  TagsOutlined, AuditOutlined, CalendarOutlined, DollarCircleOutlined,
  GiftOutlined, ClockCircleOutlined, SmileOutlined, MessageOutlined,
  EditOutlined, PlusOutlined, DeleteOutlined, DollarOutlined,
  FileTextOutlined, CheckCircleOutlined, PaperClipOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { yuan } from '../utils/format.js';
import { apiFetch, notifyDataChanged } from '../utils/api.js';

const { Text } = Typography;

const LEVELS = ['普通', '重点', 'VIP'];
const LEVEL_COLOR = { '普通': 'default', '重点': 'blue', 'VIP': 'gold' };
const SOURCES = ['转介绍', '线下拜访', '微信', '其他'];
const FOLLOW_CHANNELS = ['电话', '微信', '面谈', '其他'];
const PAY_METHODS = ['转账', '现金', '微信', '支付宝', '其他'];
const INVOICE_TYPES = ['增值税普票', '增值税专票', '电子普票'];
const INVOICE_STATUS = ['已开', '未开'];

// 千分位金额
const fmtMoney = (v) => {
  const n = Number(v);
  if (isNaN(n)) return '0.00';
  const neg = n < 0;
  const abs = Math.abs(n).toFixed(2);
  const parts = abs.split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return (neg ? '-' : '') + parts.join('.');
};
const round2 = (v) => Math.round(Number(v) * 100) / 100;
// 货量（件数）：整数千分位
const fmtQty = (v) => {
  const n = Number(v);
  if (!n) return '0';
  return Math.round(n).toLocaleString('en-US');
};
// 订单发货进度（三态）：pending 待发货 / partial 部分已发 / shipped 已发完（与后端 database.shipProgressOf 同口径）
const shipProgressOf = (o) => {
  const items = Array.isArray(o?.spec_items) && o.spec_items.length ? o.spec_items : [];
  if (o?.shipping_date) return { status: 'shipped', shipped: items.length || 1, total: items.length || 1 };
  if (items.length <= 1) return { status: 'pending', shipped: 0, total: 1 };
  const shipped = items.filter(it => it && it.shipped).length;
  if (shipped >= items.length) return { status: 'shipped', shipped, total: items.length };
  if (shipped > 0) return { status: 'partial', shipped, total: items.length };
  return { status: 'pending', shipped: 0, total: items.length };
};
// 发货状态胶囊（三态）
const shipStatusTag = (o) => {
  const p = shipProgressOf(o);
  if (p.status === 'shipped') return { color: 'green', text: '已发货' };
  if (p.status === 'partial') return { color: 'volcano', text: `部分已发 ${p.shipped}/${p.total}` };
  return { color: 'orange', text: '待发货' };
};
// 行级件数兜底：行 piece_qty → per_code_qty 映射 → 实发÷编码后缀 反推（历史订单明细行无 piece_qty 字段）
const linePieceQtyOf = (order, it) => {
  if (!it) return null;
  if (Number(it.piece_qty) > 0) return Number(it.piece_qty);
  const pcq = order && order.per_code_qty;
  if (pcq && Number(pcq[it.spec_code]) > 0) return Number(pcq[it.spec_code]);
  const sq = Number(it.spec_qty) || 0;
  if (sq > 0 && Number(it.product_quantity) > 0) return Math.round((Number(it.product_quantity) / sq) * 100) / 100;
  return null;
};

// ===== 客户编辑弹窗 =====
function CustomerModal({ open, customer, onCancel, onDone }) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) {
      form.setFieldsValue(customer ? {
        name: customer.name, contact: customer.contact, position: customer.position, phone: customer.phone,
        wechat: customer.wechat, region: customer.region, address: customer.address,
        level: customer.level, tags: customer.tags, source: customer.source, remark: customer.remark,
      } : { level: '普通', tags: [] });
    }
  }, [open, customer, form]);

  const submit = async () => {
    try {
      const v = await form.validateFields();
      setSaving(true);
      const url = customer ? `/api/crm/customers/${customer.id}` : '/api/crm/customers';
      const res = await apiFetch(url, {
        method: customer ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(v),
      }).then(r => r.json());
      setSaving(false);
      if (res.success) { message.success(customer ? '已保存' : '客户已创建'); onDone(); }
      else message.error(res.error || '保存失败');
    } catch (e) { setSaving(false); if (e && e.errorFields) return; message.error('保存失败'); }
  };

  return (
    <Modal title={customer ? '编辑客户' : '新增客户'} open={open} onCancel={onCancel} onOk={submit}
      okText="保存" confirmLoading={saving} width={640} destroyOnClose>
      <Form form={form} layout="vertical">
        <Row gutter={12}>
          <Col span={12}><Form.Item name="name" label="客户名" rules={[{ required: true, message: '客户名必填' }]}><Input placeholder="公司/门店名称" /></Form.Item></Col>
          <Col span={12}><Form.Item name="contact" label="联系人"><Input placeholder="对接人姓名" /></Form.Item></Col>
          <Col span={12}><Form.Item name="position" label="职位"><Input placeholder="如：老板 / 采购经理" /></Form.Item></Col>
          <Col span={12}><Form.Item name="phone" label="电话"><Input placeholder="手机/座机" /></Form.Item></Col>
          <Col span={12}><Form.Item name="wechat" label="微信"><Input placeholder="微信号" /></Form.Item></Col>
          <Col span={12}><Form.Item name="region" label="区域/省份"><Input placeholder="如：浙江" /></Form.Item></Col>
          <Col span={12}><Form.Item name="level" label="客户等级"><Select options={LEVELS.map(l => ({ value: l, label: l }))} /></Form.Item></Col>
          <Col span={12}><Form.Item name="source" label="来源"><Select options={SOURCES.map(s => ({ value: s, label: s }))} allowClear /></Form.Item></Col>
          <Col span={24}><Form.Item name="tags" label="标签"><Select mode="tags" placeholder="回车添加标签，如：月结、账期30天" /></Form.Item></Col>
          <Col span={24}><Form.Item name="address" label="地址"><Input placeholder="收货/办公地址" /></Form.Item></Col>
          <Col span={24}><Form.Item name="remark" label="备注"><Input.TextArea rows={2} placeholder="客户偏好、结算约定等" /></Form.Item></Col>
        </Row>
      </Form>
    </Modal>
  );
}

// ===== 成交下单弹窗（复用商品成本表，进订单管理）=====
function OrderModal({ open, customer, prodOpts, customers, onCancel, onDone }) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const fixedCustomer = customer && customer.id ? customer : null; // 固定客户（详情页传入）
  const [selCustId, setSelCustId] = useState(null);
  const custOptions = (customers || []).filter(c => c.name !== '待归属').map(c => ({ value: c.id, label: c.name }));
  // 总成交价：自动求和 + 可手动覆盖
  const [totalPrice, setTotalPrice] = useState(0);
  const [totalTouched, setTotalTouched] = useState(false);
  const items = Form.useWatch('items', form) || [];
  const autoTotal = items.reduce((s, it) => s + (Number(it && it.subtotal) || 0), 0);

  useEffect(() => {
    if (open) {
      form.setFieldsValue({ items: [{ piece_qty: 1 }], paid_amount: 0, is_dropship: false });
      setSelCustId(fixedCustomer ? fixedCustomer.id : null);
      setTotalPrice(0); setTotalTouched(false);
    }
  }, [open, form, fixedCustomer]);

  // 未手动改总价时，总价随各行小计自动求和
  useEffect(() => {
    if (!totalTouched) setTotalPrice(autoTotal);
  }, [autoTotal, totalTouched]);

  const submit = async () => {
    try {
      const v = await form.validateFields();
      const cust = fixedCustomer || (customers || []).find(c => c.id === selCustId);
      if (!cust) { message.error('请选择客户'); return; }
      const list = (v.items || []).filter(it => it && it.spec_code);
      if (!list.length) { message.error('请至少选择一件商品'); return; }
      // 同商品去重校验（不允许重复）
      const seen = new Set();
      for (const it of list) {
        if (seen.has(it.spec_code)) { message.error(`商品 ${it.spec_code} 重复，请检查`); return; }
        seen.add(it.spec_code);
      }
      const finalTotal = Number(totalPrice) || autoTotal;
      if (finalTotal <= 0) { message.error('总成交价需大于 0'); return; }
      setSaving(true);
      const res = await apiFetch('/api/orders/manual', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          spec_items: list.map(it => ({
            spec_code: it.spec_code,
            piece_qty: Number(it.piece_qty) > 0 ? Number(it.piece_qty) : 1,
            subtotal: Number(it.subtotal) || 0,
            is_dropship: !!it.is_dropship,
          })),
          piece_qty: list.reduce((s, it) => s + (Number(it.piece_qty) > 0 ? Number(it.piece_qty) : 1), 0),
          order_price: finalTotal,
          paid_amount: v.paid_amount || 0,
          order_status: 'pending',
          region: v.region, courier_company: v.courier_company, tracking_no: v.tracking_no,
          remark: v.remark, is_dropship: v.is_dropship,
          shop: cust.name, customer_id: cust.id, channel: 'offline',
        }),
      }).then(r => r.json());
      setSaving(false);
      if (res.success) {
        message.success(`已建立待发货订单 ${res.data.platform_order_no}，预算利润 ¥${yuan(res.data.profit)}（发货后计入财务）`);
        onDone(); notifyDataChanged();
      } else message.error(res.error || '录入失败');
    } catch (e) { setSaving(false); if (e && e.errorFields) return; message.error('录入失败'); }
  };

  return (
    <Modal title={`新建订单 · ${fixedCustomer ? fixedCustomer.name : '选择客户'}`} open={open} onCancel={onCancel} onOk={submit}
      okText="建立待发货订单" confirmLoading={saving} width={640} destroyOnClose>
      <Form form={form} layout="vertical">
        <Row gutter={12}>
          {!fixedCustomer && (
            <Col span={24}>
              <Form.Item label="客户" required>
                <Select placeholder="选择客户（排除待归属）" value={selCustId} onChange={setSelCustId}
                  showSearch optionFilterProp="label" options={custOptions} />
              </Form.Item>
            </Col>
          )}
          <Col span={24}>
            <Form.List name="items">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }, idx) => (
                    <Card key={key} size="small" style={{ marginBottom: 8, background: '#FAFAFA' }} styles={{ body: { padding: '8px 12px' } }}>
                      <Row gutter={8} align="middle">
                        <Col span={9}>
                          <Form.Item {...restField} name={[name, 'spec_code']} label={idx === 0 ? '商品' : ''} rules={[{ required: true, message: '选商品' }]} style={{ marginBottom: 0 }}>
                            <Select options={prodOpts} placeholder="搜索商品规格编码" showSearch optionFilterProp="label" />
                          </Form.Item>
                        </Col>
                        <Col span={4}>
                          <Form.Item {...restField} name={[name, 'piece_qty']} label={idx === 0 ? '件数' : ''} rules={[{ required: true, message: '件数' }]} style={{ marginBottom: 0 }}>
                            <InputNumber min={1} precision={0} style={{ width: '100%' }} placeholder="件数" />
                          </Form.Item>
                        </Col>
                        <Col span={5}>
                          <Form.Item {...restField} name={[name, 'subtotal']} label={idx === 0 ? '小计（元）' : ''} rules={[{ required: true, message: '小计' }]} style={{ marginBottom: 0 }}>
                            <InputNumber min={0} precision={2} style={{ width: '100%' }} placeholder="金额" />
                          </Form.Item>
                        </Col>
                        <Col span={3} style={{ textAlign: 'center', paddingTop: idx === 0 ? 28 : 0 }}>
                          <Form.Item {...restField} name={[name, 'is_dropship']} valuePropName="checked" style={{ marginBottom: 0 }} tooltip="外面代发，不扣系统库存">
                            <Checkbox style={{ fontSize: 12 }}>代发</Checkbox>
                          </Form.Item>
                        </Col>
                        <Col span={3} style={{ textAlign: 'right', paddingTop: idx === 0 ? 24 : 0 }}>
                          {fields.length > 1
                            ? <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(name)} />
                            : <span style={{ color: '#bfbfbf' }}>—</span>}
                        </Col>
                      </Row>
                    </Card>
                  ))}
                  <Button type="dashed" block icon={<PlusOutlined />} onClick={() => add({ piece_qty: 1 })} style={{ marginBottom: 8 }}>
                    添加商品
                  </Button>
                </>
              )}
            </Form.List>
          </Col>
          <Col span={24}>
            <div style={{ background: '#FFF9F6', borderRadius: 6, padding: '8px 12px', fontSize: 12, color: 'var(--brand)', marginBottom: 8 }}>
              新建订单为「待发货」状态，只算预算利润、不扣库存、不进财务；上传单号并点「发货」后才扣库存并进入财务计算。
            </div>
          </Col>
          <Col span={12}>
            <Form.Item label="总成交价（元）" required>
              <InputNumber min={0} precision={2} style={{ width: '100%' }} value={totalPrice}
                onChange={v => { setTotalTouched(true); setTotalPrice(v); }} />
            </Form.Item>
            <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: -8, marginBottom: 8 }}>
              各行小计之和：<b style={{ color: 'var(--brand)' }}>¥{fmtMoney(autoTotal)}</b>
              {totalTouched && <a style={{ marginLeft: 10 }} onClick={() => { setTotalTouched(false); setTotalPrice(autoTotal); }}>恢复自动求和</a>}
            </div>
          </Col>
          <Col span={12}><Form.Item name="paid_amount" label="本次已收/定金（元）"><InputNumber min={0} precision={2} style={{ width: '100%' }} /></Form.Item></Col>
          <Col span={8}><Form.Item name="region" label="收货省份（算运费）"><Input placeholder="如：四川" /></Form.Item></Col>
          <Col span={8}><Form.Item name="courier_company" label="快递公司"><Input placeholder="如：极兔快递" /></Form.Item></Col>
          <Col span={16}><Form.Item name="tracking_no" label="快递单号"><Input placeholder="多个单号用空格分隔" /></Form.Item></Col>
          <Col span={8}><Form.Item name="is_dropship" label="代发（不扣库存）" valuePropName="checked"><Switch /></Form.Item></Col>
          <Col span={24}><Form.Item name="remark" label="备注"><Input.TextArea rows={2} /></Form.Item></Col>
        </Row>
      </Form>
    </Modal>
  );
}

// ===== 订单发货弹窗（待发货 → 已发货，扣库存 + 进入财务；多产品订单支持勾选行分批发货） =====
function ShipModal({ open, order, onCancel, onDone }) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [checked, setChecked] = useState([]); // 勾选要发货的 spec_code
  const items = Array.isArray(order?.spec_items) && order.spec_items.length ? order.spec_items : [];
  const pendingItems = items.filter(it => !it.shipped); // 剩余待发货的行
  const isMulti = items.length > 1;

  useEffect(() => {
    if (open) {
      form.setFieldsValue({ shipping_date: dayjs(), courier_company: order?.courier_company || '', tracking_no: order?.tracking_no || '' });
      setChecked(pendingItems.map(it => it.spec_code)); // 默认全选待发行
    }
  }, [open, order, form]);

  const submit = async () => {
    try {
      const v = await form.validateFields();
      setSaving(true);
      const body = {
        platform_order_no: order.platform_order_no,
        courier_company: v.courier_company,
        tracking_no: v.tracking_no,
        shipping_date: v.shipping_date ? v.shipping_date.format('YYYY-MM-DD') : undefined,
      };
      if (isMulti) {
        if (!checked.length) { setSaving(false); message.warning('请勾选本次要发货的产品'); return; }
        body.line_codes = checked;
      }
      const res = await apiFetch('/api/orders/ship', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }).then(r => r.json());
      setSaving(false);
      if (res.success) {
        message.success(res.data && res.data.partial ? '已部分发货，剩余产品待后续发出' : '已发货，订单进入财务计算');
        onDone();
      } else message.error(res.error || '发货失败');
    } catch (e) { setSaving(false); if (e && e.errorFields) return; message.error('发货失败'); }
  };

  return (
    <Modal title={`订单发货 · ${order?.platform_order_no || ''}`} open={open} onCancel={onCancel} onOk={submit}
      okText="确认发货" confirmLoading={saving} width={isMulti ? 560 : 480} destroyOnClose centered zIndex={2000}>
      <Form form={form} layout="vertical">
        {isMulti && pendingItems.length > 0 && (
          <div style={{ marginBottom: 16, border: '1px solid #F0F0F0', borderRadius: 8, padding: '10px 14px', background: '#FFF9F6' }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: '#26201C' }}>
              选择本次发货的产品（共 {pendingItems.length} 种待发）
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <span style={{ fontSize: 12, color: '#8c8c8c' }}>已勾选 {checked.length} / {pendingItems.length}</span>
              <Space size={4}>
                <Button size="small" type="link" onClick={() => setChecked(pendingItems.map(it => it.spec_code))}>全选</Button>
                <Button size="small" type="link" onClick={() => setChecked([])}>清空</Button>
              </Space>
            </div>
            {pendingItems.map(it => (
              <Checkbox key={it.spec_code} checked={checked.includes(it.spec_code)}
                onChange={e => setChecked(prev => e.target.checked ? [...prev, it.spec_code] : prev.filter(c => c !== it.spec_code))}
                style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>
                {it.product_name || it.spec_code}
                <span style={{ color: '#8c8c8c', fontSize: 12 }}> · 实发 {it.product_quantity}</span>
              </Checkbox>
            ))}
            {checked.length > 0 && checked.length < pendingItems.length && (
              <div style={{ fontSize: 12, color: '#D85A30', marginTop: 6 }}>
                只发勾选的 {checked.length} 种，剩余 {pendingItems.length - checked.length} 种保持「待发货」
              </div>
            )}
          </div>
        )}
        <Form.Item name="shipping_date" label="发货日期" rules={[{ required: true, message: '请选日期' }]}><DatePicker style={{ width: '100%' }} /></Form.Item>
        <Form.Item name="courier_company" label="快递公司"><Input placeholder="如：极兔快递" /></Form.Item>
        <Form.Item name="tracking_no" label="快递单号"><Input placeholder="选填，用于物流追踪" /></Form.Item>
      </Form>
    </Modal>
  );
}

// ===== 订单运费/辅材修改弹窗（手动覆盖 or 恢复自动计算） =====
function CostEditModal({ open, order, onCancel, onDone }) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const shippingMode = Form.useWatch('shipping_mode', form);
  const packagingMode = Form.useWatch('packaging_mode', form);

  useEffect(() => {
    if (open && order) {
      form.setFieldsValue({
        shipping_mode: order.manual_shipping != null ? 'manual' : 'auto',
        packaging_mode: order.manual_packaging != null ? 'manual' : 'auto',
        manual_shipping: order.manual_shipping != null ? order.manual_shipping : (Number(order.shipping_fee) || 0),
        manual_packaging: order.manual_packaging != null ? order.manual_packaging : (Number(order.packaging_cost) || 0),
      });
    }
  }, [open, order, form]);

  const submit = async () => {
    try {
      const v = await form.validateFields();
      setSaving(true);
      const res = await apiFetch('/api/orders/manual-fields', {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          platform_order_no: order.platform_order_no,
          // 手动模式传数值（0=客户付）；自动模式传 '' = 恢复系统自动计算
          shipping: v.shipping_mode === 'manual' ? (v.manual_shipping ?? 0) : '',
          packaging: v.packaging_mode === 'manual' ? (v.manual_packaging ?? 0) : '',
        }),
      }).then(r => r.json());
      setSaving(false);
      if (res.success) { message.success('已保存，利润已联动重算'); onDone(); }
      else message.error(res.error || '保存失败');
    } catch (e) { setSaving(false); if (e && e.errorFields) return; message.error('保存失败'); }
  };

  return (
    <Modal title={`修改运费/辅材 · ${order?.platform_order_no || ''}`} open={open} onCancel={onCancel} onOk={submit}
      okText="保存" confirmLoading={saving} width={520} destroyOnClose>
      <div style={{ background: '#FFF9F6', borderRadius: 8, padding: '10px 14px', marginBottom: 16, border: '1px solid #FFE4D6', fontSize: 12, color: '#8c8c8c', lineHeight: 1.7 }}>
        系统默认按重量+区域<b style={{ color: 'var(--brand)' }}>自动计算</b>运费/辅材；若该单<b style={{ color: 'var(--brand)' }}>运费或辅材由客户支付</b>，请切到「手动填写」并填 0（记 0、不计入成本）。选「自动计算」则恢复系统自动值。
      </div>
      <Form form={form} layout="vertical">
        <Form.Item name="shipping_mode" label="运费方式" style={{ marginBottom: 12 }}>
          <Segmented block options={[{ label: '自动计算', value: 'auto' }, { label: '手动填写', value: 'manual' }]} />
        </Form.Item>
        {shippingMode === 'manual' && (
          <Form.Item name="manual_shipping" label="运费金额（元）" rules={[{ required: true, message: '请填运费' }]}
            extra="客户自付运费请填 0（运费记 0，不计入成本）">
            <InputNumber min={0} precision={2} style={{ width: '100%' }} placeholder="0 = 客户付运费" />
          </Form.Item>
        )}
        <Form.Item name="packaging_mode" label="辅材方式" style={{ marginBottom: 12 }}>
          <Segmented block options={[{ label: '自动计算', value: 'auto' }, { label: '手动填写', value: 'manual' }]} />
        </Form.Item>
        {packagingMode === 'manual' && (
          <Form.Item name="manual_packaging" label="辅材金额（元）" rules={[{ required: true, message: '请填辅材' }]}
            extra="客户自付辅材请填 0（辅材记 0，不计入成本）">
            <InputNumber min={0} precision={2} style={{ width: '100%' }} placeholder="0 = 客户付辅材" />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
}

// ===== 订单详情 / 编辑弹窗（宽长框：上「订单信息」展示 + 下「编辑」表单，一次保存）=====
// 支持分批发货：状态栏「发货」按钮打开发货弹窗（勾选产品行），发货后自刷新订单数据
function EditOrderModal({ open, order: orderProp, prodOpts, allCustomers, onCancel, onDone, onChanged }) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [cur, setCur] = useState(null);            // 最新订单数据（分批发货后自刷新）
  const [shipRows, setShipRows] = useState({});    // 行级发货填写：{ spec_code: { tracking_no, ship_fee } }
  const [shipCourier, setShipCourier] = useState(''); // 订单级快递公司（一单一个快递，发货时同步）
  const [dropRows, setDropRows] = useState({});    // 行级代发开关（仅记录改动行）：{ spec_code: bool }
  const order = cur || orderProp;                  // 展示/编辑统一用最新数据
  const packagingMode = Form.useWatch('packaging_mode', form);
  // 行级回款：明细表「回款」按钮 → 弹窗自动按该行售卖价预填金额，登记后冲抵订单应收
  const [payOpen, setPayOpen] = useState(false);
  const [paySaving, setPaySaving] = useState(false);
  const [payInit, setPayInit] = useState(0);       // 自动计算的回款金额
  const [payForm] = Form.useForm();
  const [payAccounts, setPayAccounts] = useState([]);

  // 发货后拉取订单最新数据（状态/发货列实时更新）
  const refreshOrder = async () => {
    try {
      const res = await apiFetch(`/api/orders/detail?platform_order_no=${encodeURIComponent(orderProp.platform_order_no)}`).then(r => r.json());
      if (res.success && res.data) setCur(res.data);
    } catch (e) { /* 静默 */ }
  };

  const custOptions = (allCustomers || []).filter(c => c.name !== '待归属').map(c => ({ value: c.id, label: c.name }));
  // 多商品订单（spec_code 逗号分隔 或 多个 spec_items）：每行件数独立（per_code_qty）
  const multiCodes = order?.spec_code ? String(order.spec_code).split(/[,，、]/).filter(Boolean).length : 0;
  const isMulti = multiCodes > 1 || (Array.isArray(order?.spec_items) && order.spec_items.length > 1);
  // 手动改件数模式：多商品订单可逐行修正件数，保存时传 line_items 让后端写 per_code_qty 并联动重算
  const [qtyEditing, setQtyEditing] = useState(false);
  const [lineRows, setLineRows] = useState([]);

  useEffect(() => {
    if (open && orderProp) {
      setCur(orderProp);
      setShipRows({});
      setDropRows({});
      setShipCourier(orderProp.courier_company || '');
      const o = orderProp;
      // 件数显示「总件数」（= order_quantity，单编码时等于每编码件数）
      form.setFieldsValue({
        spec_code: o.spec_code,
        piece_qty: Number(o.order_quantity) > 0 ? Number(o.order_quantity) : (Number(o.piece_qty) || 1),
        order_price: Number(o.order_price) || 0,
        packaging_mode: o.manual_packaging != null ? 'manual' : 'auto',
        manual_packaging: o.manual_packaging != null ? o.manual_packaging : (Number(o.packaging_cost) || 0),
        customer_id: o.customer_id != null ? o.customer_id : undefined,
        remark: o.remark || '',
      });
      // 初始化逐行件数（多商品订单；用兜底逻辑取真实件数，防历史行无 piece_qty 显示成 1）
      const its = Array.isArray(o.spec_items) ? o.spec_items : [];
      setLineRows(its.map(it => ({ spec_code: it.spec_code, piece_qty: linePieceQtyOf(o, it) || 1 })));
      setQtyEditing(false);
    }
  }, [open, orderProp, form]);

  const submit = async () => {
    try {
      const v = await form.validateFields();
      setSaving(true);
      const payload = {
        platform_order_no: order.platform_order_no,
        order_price: v.order_price,
        // 运费不在表单里改：统一走上方明细行「发货运费」（分批发货）或「改运费/辅材」入口，不传=保持不变
        packaging: v.packaging_mode === 'manual' ? (v.manual_packaging ?? 0) : '',
        customer_id: v.customer_id != null ? v.customer_id : (order.customer_id != null ? order.customer_id : null),
        remark: v.remark || '',
      };
      // 多商品订单：商品编码不可改；若开启「改件数」则传逐行件数（line_items → 后端写 per_code_qty）
      if (!isMulti) { payload.spec_code = v.spec_code; payload.piece_qty = v.piece_qty; }
      else if (qtyEditing && lineRows.length) {
        const bad = lineRows.find(r => !(Number(r.piece_qty) > 0));
        if (bad) { message.error(`编码 ${bad.spec_code} 的件数必须大于 0`); setSaving(false); return; }
        payload.line_items = lineRows.map(r => ({ spec_code: r.spec_code, piece_qty: Number(r.piece_qty) }));
      }
      // 行级代发开关：只传改动过的行（后端写 spec_items[].is_dropship 并按差额调整库存——
      // 勾选代发=该行未扣过库存则加回；取消代发=重新扣库存），与主表单同一次保存
      const dropLines = items
        .filter(it => dropRows[it.spec_code] !== undefined && !!dropRows[it.spec_code] !== !!it.is_dropship)
        .map(it => ({ spec_code: it.spec_code, is_dropship: !!dropRows[it.spec_code] }));
      if (dropLines.length) payload.dropship_lines = dropLines;
      const res = await apiFetch('/api/orders/edit', {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }).then(r => r.json());
      if (!res.success) { setSaving(false); message.error(res.error || '保存失败'); return; }
      // 分批发货/行级更新：收集所有「发货单号非空」的行（未发行=发货并扣库存；已发行=仅更新单号/运费）
      let shipMsg = '';
      const shipTargets = items.filter(it => {
        const sr = shipRows[it.spec_code] || {};
        return !!String(sr.tracking_no ?? it.tracking_no ?? '').trim();
      });
      if (shipTargets.length) {
        const shipRes = await apiFetch('/api/orders/ship', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            platform_order_no: order.platform_order_no,
            courier_company: shipCourier.trim(),
            line_details: shipTargets.map(it => {
              const sr = shipRows[it.spec_code] || {};
              return {
                spec_code: it.spec_code,
                tracking_no: String(sr.tracking_no ?? it.tracking_no ?? '').trim(),
                ship_fee: sr.ship_fee != null ? Number(sr.ship_fee) : (it.ship_fee != null ? Number(it.ship_fee) : null),
              };
            }),
          }),
        }).then(r => r.json());
        if (!shipRes.success) { setSaving(false); message.error(shipRes.error || '发货失败'); return; }
        const newShipped = shipTargets.filter(it => !it.shipped).length;
        if (newShipped) shipMsg = `，其中 ${newShipped} 种产品已发货`;
      }
      setSaving(false);
      message.success('已保存' + shipMsg + '，成本利润已联动重算');
      onDone();
    } catch (e) { setSaving(false); if (e && e.errorFields) return; message.error('保存失败'); }
  };

  // ===== 只读展示数据 =====
  const items = Array.isArray(order?.spec_items) && order.spec_items.length ? order.spec_items : [];
  const shipProg = shipProgressOf(order); // 发货进度三态
  const receivable = (Number(order?.order_price) || 0) - (Number(order?.paid_amount) || 0);

  // ===== 行级回款 =====
  useEffect(() => {
    if (payOpen) {
      payForm.setFieldsValue({ amount: payInit, date: dayjs(), method: '转账', remark: '' });
      loadPayAccounts();
    }
  }, [payOpen, payInit, payForm]);

  const loadPayAccounts = async () => {
    try {
      const res = await apiFetch('/api/cash-account').then(r => r.json());
      const list = (res && res.accounts) || [];
      setPayAccounts(list);
      const def = list.find(a => String(a.name || '').includes('淮安乐兜')) || list[0];
      if (def) payForm.setFieldValue('cash_account_id', def.id);
    } catch (e) { /* 忽略 */ }
  };

  const openPay = (lineAmount) => {
    if (order?.customer_id == null) { message.warning('该订单未归属客户，请先在下方「归属客户」选择客户并保存，再登记回款'); return; }
    if (receivable <= 0.001) { message.info('该订单已结清，无需回款'); return; }
    // 自动计算：默认 = 该行售卖价；超过订单应收则取应收（避免超收）
    const amt = lineAmount != null && lineAmount > 0 ? Math.min(lineAmount, receivable) : receivable;
    setPayInit(round2(amt));
    setPayOpen(true);
  };

  const submitPay = async () => {
    try {
      const v = await payForm.validateFields();
      setPaySaving(true);
      const res = await apiFetch('/api/crm/payments', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_id: order.customer_id,
          amount: v.amount,
          date: v.date.format('YYYY-MM-DD'),
          method: v.method,
          order_nos: [order.platform_order_no],
          remark: v.remark || '',
          cash_account_id: v.cash_account_id,
        }),
      }).then(r => r.json());
      setPaySaving(false);
      if (res.success) {
        message.success(`回款 ¥${fmtMoney(v.amount)} 已登记，订单应收已冲抵`);
        setPayOpen(false);
        refreshOrder();   // 弹窗内已收/应收实时更新
        onChanged && onChanged(); // 父页面列表/统计联动刷新
      } else message.error(res.error || '登记失败');
    } catch (e) { setPaySaving(false); if (e && e.errorFields) return; message.error('登记失败'); }
  };
  const cost = (Number(order?.product_cost) || 0) + (Number(order?.shipping_fee) || 0) + (Number(order?.packaging_cost) || 0);
  const profitVal = Number(order?.profit) || 0;
  const margin = (Number(order?.order_price) || 0) > 0 ? profitVal / (Number(order?.order_price) || 0) * 100 : 0;

  const statBlock = (label, val, color, extra) => (
    <div style={{ background: '#fff', borderRadius: 8, padding: '10px 14px', border: '1px solid #F0F0F0', textAlign: 'left' }}>
      <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color, fontFamily: 'ui-monospace, Menlo, monospace', lineHeight: 1.2 }}>{val}</div>
      {extra && <div style={{ fontSize: 11, color: '#bfbfbf', marginTop: 2 }}>{extra}</div>}
    </div>
  );

  return (
    <Modal
      title={<Space size={8} wrap><b style={{ fontSize: 16 }}>订单详情 · {order?.platform_order_no || ''}</b></Space>}
      open={open} onCancel={onCancel} onOk={submit}
      okText="保存修改" confirmLoading={saving} width="94%" destroyOnClose centered
      style={{ maxWidth: 1360 }}
      styles={{ body: { maxHeight: '88vh', overflow: 'auto', padding: '20px 24px' } }}
    >
      {/* ===== 状态栏 ===== */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
        <Space size={8} wrap>
          <Tag color={shipStatusTag(order).color}>{shipStatusTag(order).text}</Tag>
          {receivable > 0 ? <Tag color="orange">待回款</Tag> : <Tag color="green">已结清</Tag>}
          {order?.is_dropship && <Tag color="cyan">整单代发</Tag>}
          {!order?.is_dropship && (order?.spec_items || []).some(it => it.is_dropship) && <Tag color="cyan">含代发</Tag>}
        </Space>
        <span style={{ fontSize: 13, color: profitVal >= 0 ? '#3F8600' : '#CF1322', fontWeight: 600 }}>
          利润 {profitVal >= 0 ? '+' : ''}¥{fmtMoney(profitVal)} · 毛利率 {margin.toFixed(1)}%
        </span>
      </div>

      {/* ===== 财务指标 ===== */}
      <Row gutter={12} style={{ marginBottom: 14 }}>
        <Col span={6}>{statBlock('成交价', `¥${fmtMoney(order?.order_price)}`, 'var(--brand)')}</Col>
        <Col span={6}>{statBlock('已收', `¥${fmtMoney(order?.paid_amount)}`, '#3F8600')}</Col>
        <Col span={6}>{statBlock('应收', `¥${fmtMoney(receivable)}`, receivable > 0 ? '#CF1322' : '#3F8600')}</Col>
        <Col span={6}>{statBlock('利润', `${profitVal >= 0 ? '+' : ''}¥${fmtMoney(profitVal)}`, profitVal >= 0 ? '#3F8600' : '#CF1322')}</Col>
      </Row>

      {/* ===== 商品明细 + 物流 + 客户地址 + 成本 ===== */}
      <div style={{ background: '#FFF9F6', border: '1px solid #FFE4D6', borderRadius: 10, padding: '14px 16px', marginBottom: 18 }}>
        {/* 商品明细 */}
        <div style={{ marginBottom: 10 }}>
          {items.length > 1 && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              {qtyEditing && (
                <span style={{ fontSize: 12, color: '#8c8c8c' }}>
                  合计 <b style={{ color: 'var(--brand)' }}>{lineRows.reduce((a, r) => a + (Number(r.piece_qty) || 0), 0)}</b> 件
                </span>
              )}
              {qtyEditing ? (
                <Button size="small" onClick={() => {
                  setLineRows(items.map(it => ({ spec_code: it.spec_code, piece_qty: Number(it.piece_qty) || 1 })));
                  setQtyEditing(false);
                }}>取消改件数</Button>
              ) : (
                <Button size="small" type="primary" ghost icon={<EditOutlined />} onClick={() => setQtyEditing(true)}>手动改件数</Button>
              )}
            </div>
          )}
          {items.length >= 1 ? (
            <>
            <style>{`
.od-tb{border-collapse:separate;border-spacing:0;table-layout:fixed;width:100%;font-size:12px}
.od-tb th{background:#FAECE7;color:#993C1D;font-weight:600;padding:8px 10px;text-align:left;white-space:nowrap;position:sticky;top:0;z-index:1}
.od-tb th:first-child{border-radius:8px 0 0 8px}
.od-tb th:last-child{border-radius:0 8px 8px 0}
.od-tb td{padding:9px 10px;border-bottom:1px solid #F7E8E0;vertical-align:middle}
.od-tb tbody tr:last-child td{border-bottom:none}
.od-tb tbody tr:hover td{background:#FFFBF8}
.od-clamp2{display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;line-height:1.45;word-break:break-all}
.od-num{font-family:ui-monospace, Menlo, monospace;font-variant-numeric:tabular-nums;text-align:right;white-space:nowrap}
`}</style>
            <table className="od-tb">
              <colgroup>
                <col style={{ width: 132 }} />
                <col />
                <col style={{ width: 84 }} />
                <col style={{ width: 52 }} />
                <col style={{ width: 58 }} />
                <col style={{ width: 84 }} />
                <col style={{ width: 170 }} />
                <col style={{ width: 92 }} />
                <col style={{ width: 52 }} />
                <col style={{ width: 80 }} />
              </colgroup>
              <thead>
                <tr>
                  <th>编码</th>
                  <th>商品名称</th>
                  <th style={{ textAlign: 'right' }} title="录入订单时按商品填写的小计金额，各行之和=成交价">售卖价</th>
                  <th style={{ textAlign: 'center' }}>件数</th>
                  <th style={{ textAlign: 'center' }} title="实发数量 = 编码后缀 × 件数">数量</th>
                  <th style={{ textAlign: 'right' }} title="该行商品成本金额，各行之和=商品成本">成本价</th>
                  <th>发货单号</th>
                  <th>发货运费</th>
                  <th style={{ textAlign: 'center' }} title="勾选=外面代发，不扣系统库存">代发</th>
                  <th style={{ textAlign: 'center' }} title="点击后自动按该行售卖价预填回款金额，登记后冲抵订单应收">回款</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  // 行级售卖价：录单时每行填的小计（order.items[].subtotal）；历史单无明细时单编码兜底=成交价
                  const subMap = {};
                  for (const li of (Array.isArray(order?.items) ? order.items : [])) {
                    if (li && li.spec_code) subMap[String(li.spec_code).trim()] = Number(li.subtotal) || 0;
                  }
                  const singleCode = String(order?.spec_code || '').split(/[,，、]/).filter(Boolean).length === 1;
                  return items.map((it, i) => {
                  const codeKey = String(it.spec_code || '').trim();
                  const lineSub = subMap[codeKey] != null ? subMap[codeKey] : (items.length === 1 && singleCode ? Number(order?.order_price) || 0 : null);
                  const sr = shipRows[it.spec_code] || {};
                  return (
                    <tr key={i}>
                      <td>
                        <div className="od-clamp2" title={it.spec_code}
                          style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 11, color: '#2F54EB' }}>{it.spec_code}</div>
                      </td>
                      <td>
                        <div className="od-clamp2" title={it.product_name || ''} style={{ color: '#26201C' }}>{it.product_name || '-'}</div>
                      </td>
                      <td className="od-num" style={{ color: '#D85A30', fontWeight: 600 }}>{lineSub != null ? '¥' + fmtMoney(lineSub) : '-'}</td>
                      <td style={{ textAlign: 'center', color: '#26201C' }}>
                        {qtyEditing ? (
                          <InputNumber size="small" min={0.01} value={lineRows[i]?.piece_qty}
                            onChange={v => setLineRows(rows => rows.map((r, ri) => ri === i ? { ...r, piece_qty: v } : r))}
                            style={{ width: '100%' }} />
                        ) : (linePieceQtyOf(order, it) ?? '-')}
                      </td>
                      <td style={{ textAlign: 'center', color: '#26201C' }}>{it.product_quantity}</td>
                      <td className="od-num" style={{ color: '#5F5E5A' }}>{it.product_cost != null ? '¥' + fmtMoney(it.product_cost) : '-'}</td>
                      <td>
                        <Input size="small" placeholder="填单号后发货"
                          value={sr.tracking_no ?? it.tracking_no ?? ''}
                          onChange={e => setShipRows(prev => ({ ...prev, [it.spec_code]: { ...(prev[it.spec_code] || {}), tracking_no: e.target.value } }))}
                          style={{ width: '100%' }} disabled={qtyEditing} />
                      </td>
                      <td>
                        <InputNumber size="small" placeholder="选填" min={0}
                          value={sr.ship_fee ?? (it.ship_fee != null ? it.ship_fee : undefined)}
                          onChange={v => setShipRows(prev => ({ ...prev, [it.spec_code]: { ...(prev[it.spec_code] || {}), ship_fee: v } }))}
                          style={{ width: '100%' }} disabled={qtyEditing} />
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {order.is_dropship
                          ? <Tag color="cyan" style={{ marginRight: 0 }}>整单</Tag>
                          : <Switch size="small" checked={dropRows[it.spec_code] !== undefined ? !!dropRows[it.spec_code] : !!it.is_dropship}
                              onChange={v => setDropRows(prev => ({ ...prev, [it.spec_code]: v }))} disabled={qtyEditing} />}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <Button size="small" type="primary" ghost icon={<DollarOutlined />}
                          onClick={() => openPay(lineSub)} disabled={qtyEditing}
                          title={lineSub != null ? `按该行售卖价 ¥${fmtMoney(lineSub)} 预填回款` : '按订单应收预填回款'}>回款</Button>
                      </td>
                    </tr>
                  );
                  });
                })()}
              </tbody>
            </table>
            </>
          ) : (
            <div style={{ fontSize: 13, color: '#5F5E5A', fontWeight: 500 }}>
              {order?.product_name || '-'}
              {order?.piece_qty ? <span style={{ color: '#bfbfbf', marginLeft: 6 }}>× {order.piece_qty}</span> : null}
            </div>
          )}
        </div>

        {/* 物流 + 客户地址 */}
        <div style={{ fontSize: 12, color: '#595959', lineHeight: 1.7, marginBottom: 6 }}>
          <Space size={16} wrap>
            {order?.shipping_date && <span>📅 发货 {order.shipping_date}</span>}
            {(order?.region_view || order?.order_region) && <span>📍 {order.region_view || order.order_region}</span>}
            {order?.courier_company && <span>🚚 {order.courier_company}</span>}
            {order?.tracking_no && <span style={{ fontFamily: 'ui-monospace, Menlo, monospace' }}>单号 {order.tracking_no}</span>}
          </Space>
        </div>
        {/* 快递公司填写（未发完时显示，一单一个快递；发货时同步到订单级） */}
        {shipProg.status !== 'shipped' && (
          <div style={{ fontSize: 12, color: '#595959', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ flexShrink: 0 }}>🚚 快递公司</span>
            <Input size="small" placeholder="选填，如：极兔快递" value={shipCourier}
              onChange={e => setShipCourier(e.target.value)} style={{ width: 180 }} />
          </div>
        )}
        {(order?.customer_name || order?.address) && (
          <div style={{ fontSize: 12, color: '#595959', marginBottom: 6 }}>
            {order?.customer_name && <span style={{ marginRight: 10 }}><b>客户：</b>{order.customer_name}</span>}
            {order?.address && <span style={{ color: '#8c8c8c' }}>📍 {order.address}</span>}
          </div>
        )}

        {/* 成本明细 */}
        <div style={{ fontSize: 12, color: '#8c8c8c', borderTop: '1px dashed #FFD9C4', paddingTop: 8, lineHeight: 1.8 }}>
          成本：商品 <b style={{ color: '#595959' }}>¥{fmtMoney(order?.product_cost)}</b> + 运费 <b style={{ color: '#595959' }}>¥{fmtMoney(order?.shipping_fee)}</b> + 辅材 <b style={{ color: '#595959' }}>¥{fmtMoney(order?.packaging_cost)}</b> = <b style={{ color: '#26201C' }}>¥{fmtMoney(cost)}</b>
          {(order?.manual_shipping != null || order?.manual_packaging != null) && <Tag color="orange" style={{ marginLeft: 8, fontSize: 10, lineHeight: '16px' }}>手动</Tag>}
        </div>
      </div>

      {/* ===== 编辑表单 ===== */}
      <div style={{ fontSize: 14, fontWeight: 600, color: '#26201C', margin: '4px 0 8px', paddingLeft: 8, borderLeft: '3px solid var(--brand)' }}>编辑信息</div>
      <div style={{ fontSize: 12, color: '#bfbfbf', marginBottom: 14 }}>保存后自动联动重算成本 / 实发 / 利润；发货在上方明细行填单号（运费随行填写）。</div>
      <Form form={form} layout="vertical">
        {!isMulti && (
          <Row gutter={16}>
            <Col span={16}>
              <Form.Item name="spec_code" label="商品（规格编码）" rules={[{ required: true, message: '请选择商品' }]}>
                <Select options={prodOpts} placeholder="搜索商品规格编码" showSearch optionFilterProp="label" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="piece_qty" label="件数（总件数）" rules={[{ required: true, message: '件数' }]}>
                <InputNumber min={1} precision={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
        )}
        <Row gutter={16}>
          <Col span={6}>
            <Form.Item name="order_price" label="成交价（元）" rules={[{ required: true, message: '成交价' }]}>
              <InputNumber min={0} precision={2} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="customer_id" label="归属客户">
              <Select options={custOptions} placeholder="留空 = 待归属" showSearch optionFilterProp="label" allowClear />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="packaging_mode" label="辅材方式">
              <Segmented block options={[{ label: '自动计算', value: 'auto' }, { label: '手动填写', value: 'manual' }]} />
            </Form.Item>
          </Col>
          <Col span={6}>
            {packagingMode === 'manual' ? (
              <Form.Item name="manual_packaging" label="辅材金额（元）" rules={[{ required: true, message: '请填辅材' }]}>
                <InputNumber min={0} precision={2} style={{ width: '100%' }} placeholder="0 = 客户付辅材" />
              </Form.Item>
            ) : (
              <Form.Item label="当前辅材（自动）">
                <div style={{ color: 'var(--brand)', fontWeight: 600, lineHeight: '32px' }}>¥{fmtMoney(order?.packaging_cost)}</div>
              </Form.Item>
            )}
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item name="remark" label="备注"><Input.TextArea rows={2} placeholder="备注信息" /></Form.Item>
          </Col>
        </Row>
      </Form>

      {/* ===== 行级回款弹窗（金额自动按售卖价/应收预填） ===== */}
      <Modal title={`登记回款 · ${order?.platform_order_no || ''}`} open={payOpen} onCancel={() => setPayOpen(false)}
        onOk={submitPay} okText="登记" confirmLoading={paySaving} width={520} centered
        styles={{ body: { paddingTop: 16 } }}>
        <div style={{ background: '#FFF8F4', border: '1px solid #F5D8CC', borderRadius: 6, padding: '8px 12px', fontSize: 12, marginBottom: 12 }}>
          订单成交 <b style={{ color: '#26201C' }}>¥{fmtMoney(order?.order_price)}</b> · 已收 <b style={{ color: '#3F8600' }}>¥{fmtMoney(order?.paid_amount)}</b> · 应收 <b style={{ color: '#D85A30' }}>¥{fmtMoney(receivable)}</b>
          <span style={{ color: '#8c8c8c', marginLeft: 8 }}>（金额已按明细行自动预填，可修改）</span>
        </div>
        <Form form={payForm} layout="vertical">
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="amount" label="回款金额（元）" rules={[{ required: true, message: '必填' }]}>
                <InputNumber min={0.01} precision={2} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="date" label="回款日期" rules={[{ required: true, message: '必填' }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="method" label="回款方式">
                <Select options={PAY_METHODS.map(m => ({ value: m, label: m }))} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="cash_account_id" label="资金账户" rules={[{ required: true, message: '请选择入账账户' }]}>
                <Select placeholder="选择入账账户" options={payAccounts.map(a => ({ value: a.id, label: a.name }))} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="remark" label="备注"><Input placeholder="选填" /></Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </Modal>
  );
}

// ===== 跟进记录弹窗 =====
function FollowupModal({ open, customer, onCancel, onDone }) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) form.setFieldsValue({ time: dayjs(), channel: '电话', done: false }); }, [open, form]);
  const submit = async () => {
    try {
      const v = await form.validateFields();
      setSaving(true);
      const res = await apiFetch('/api/crm/followups', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_id: customer.id, time: v.time.format('YYYY-MM-DD'), channel: v.channel,
          content: v.content, next_follow_at: v.next_follow_at ? v.next_follow_at.format('YYYY-MM-DD') : '', done: v.done,
        }),
      }).then(r => r.json());
      setSaving(false);
      if (res.success) { message.success('跟进已记录'); onDone(); } else message.error(res.error || '保存失败');
    } catch (e) { setSaving(false); if (e && e.errorFields) return; message.error('保存失败'); }
  };
  return (
    <Modal title={`新增跟进 · ${customer.name}`} open={open} onCancel={onCancel} onOk={submit} okText="保存" confirmLoading={saving} width={520} destroyOnClose>
      <Form form={form} layout="vertical">
        <Row gutter={12}>
          <Col span={12}><Form.Item name="time" label="跟进时间" rules={[{ required: true, message: '必填' }]}><DatePicker style={{ width: '100%' }} /></Form.Item></Col>
          <Col span={12}><Form.Item name="channel" label="渠道"><Select options={FOLLOW_CHANNELS.map(c => ({ value: c, label: c }))} /></Form.Item></Col>
          <Col span={24}><Form.Item name="content" label="跟进内容" rules={[{ required: true, message: '请填写内容' }]}><Input.TextArea rows={3} placeholder="沟通要点、客户反馈、下一步动作" /></Form.Item></Col>
          <Col span={12}><Form.Item name="next_follow_at" label="下次跟进日期"><DatePicker style={{ width: '100%' }} /></Form.Item></Col>
          <Col span={12}><Form.Item name="done" label="本次跟进已完成" valuePropName="checked"><Switch /></Form.Item></Col>
        </Row>
      </Form>
    </Modal>
  );
}

// ===== 回款登记弹窗 =====
function PaymentModal({ open, customer, orders, onCancel, onDone }) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [selectedNos, setSelectedNos] = useState([]); // 已选订单号（多选）
  const [accounts, setAccounts] = useState([]);
  const payAmount = Number(Form.useWatch('amount', form)) || 0;
  useEffect(() => {
    if (open) {
      form.setFieldsValue({ date: dayjs(), method: '转账', order_nos: [] });
      setSelectedNos([]);
      loadAccounts();
    }
  }, [open, form]);

  const loadAccounts = async () => {
    try {
      const res = await apiFetch('/api/cash-account').then(r => r.json());
      const list = (res && res.accounts) || [];
      setAccounts(list);
      const def = list.find(a => String(a.name || '').includes('淮安乐兜')) || list[0];
      if (def) form.setFieldValue('cash_account_id', def.id);
    } catch (e) { /* 忽略 */ }
  };

  // 未收清订单（含所有明细），按时间倒序（新订单在前）
  const unsettled = (orders || [])
    .map(o => ({ ...o, __rec: Math.max(0, (Number(o.order_price) || 0) - (Number(o.paid_amount) || 0)) }))
    .filter(o => o.__rec > 0.001)
    .sort((a, b) => String(b.order_date || b.shipping_date || '').localeCompare(String(a.order_date || a.shipping_date || '')));

  // 已选订单的应收合计 + 分摊预览（按应收比例，最后一单吸收误差）
  const pickedOrders = unsettled.filter(o => selectedNos.includes(o.platform_order_no));
  const totalRec = pickedOrders.reduce((s, o) => s + o.__rec, 0);
  const pay = Math.min(payAmount, totalRec);
  const allocations = pickedOrders.map((o, i) => {
    let inc;
    if (i === pickedOrders.length - 1) {
      inc = Math.max(0, Math.min(o.__rec, round2(pay - pickedOrders.slice(0, i).reduce((s, x) => s + round2(pay * (x.__rec / (totalRec || 1))), 0))));
    } else {
      inc = round2(pay * (o.__rec / (totalRec || 1)));
    }
    return { no: o.platform_order_no, rec: o.__rec, inc };
  });

  const submit = async () => {
    try {
      const v = await form.validateFields();
      setSaving(true);
      const res = await apiFetch('/api/crm/payments', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_id: customer.id,
          amount: v.amount,
          date: v.date.format('YYYY-MM-DD'),
          method: v.method,
          order_nos: Array.isArray(v.order_nos) ? v.order_nos : [],
          remark: v.remark,
          cash_account_id: v.cash_account_id,
        }),
      }).then(r => r.json());
      setSaving(false);
      if (res.success) { message.success('回款已登记'); onDone(); } else message.error(res.error || '登记失败');
    } catch (e) { setSaving(false); if (e && e.errorFields) return; message.error('登记失败'); }
  };

  return (
    <Modal title={`登记回款 · ${customer.name}`} open={open} onCancel={onCancel} onOk={submit} okText="登记" confirmLoading={saving} width={720} destroyOnClose>
      <Form form={form} layout="vertical">
        <Row gutter={12}>
          <Col span={8}><Form.Item name="amount" label="回款金额（元）" rules={[{ required: true, message: '必填' }]}><InputNumber min={0.01} precision={2} style={{ width: '100%' }} placeholder="本次打款金额" /></Form.Item></Col>
          <Col span={8}><Form.Item name="date" label="回款日期" rules={[{ required: true, message: '必填' }]}><DatePicker style={{ width: '100%' }} /></Form.Item></Col>
          <Col span={8}><Form.Item name="method" label="回款方式"><Select options={PAY_METHODS.map(m => ({ value: m, label: m }))} /></Form.Item></Col>
          <Col span={24}>
            <Form.Item name="cash_account_id" label="资金账户" rules={[{ required: true, message: '请选择入账账户' }]}>
              <Select placeholder="选择入账账户" options={accounts.map(a => ({ value: a.id, label: `${a.name}（余额 ¥${fmtMoney(a.balance)}）` }))} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name="order_nos" label="关联订单（多选冲抵应收，未选则不冲订单）" extra={pickedOrders.length === 0 ? '不选则仅记客户回款，不冲抵订单应收' : `已选 ${pickedOrders.length} 单，应收合计 ¥${fmtMoney(totalRec)}`}>
              <Select
                mode="multiple"
                allowClear
                showSearch
                optionFilterProp="value"
                placeholder="可多选未收清订单，留空则不冲抵"
                popupMatchSelectWidth={false}
                listHeight={320}
                maxTagCount="responsive"
                value={selectedNos}
                onChange={(v) => { setSelectedNos(v || []); form.setFieldValue('order_nos', v || []); }}
                dropdownStyle={{ minWidth: 420, padding: 4 }}
                options={unsettled.map(o => ({
                  value: o.platform_order_no,
                  label: o.platform_order_no,
                  __total: Number(o.order_price) || 0,
                  __paid: Number(o.paid_amount) || 0,
                  __rec: o.__rec,
                }))}
                optionRender={(opt) => (
                  <div style={{ padding: '4px 2px' }}>
                    <div style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12, fontWeight: 600, color: '#26201C' }}>{opt.data.value}</div>
                    <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 2 }}>
                      成交 <b style={{ color: '#5F5E5A' }}>¥{fmtMoney(opt.data.__total)}</b>
                      <span style={{ margin: '0 4px' }}>·</span>
                      已收 <b style={{ color: '#3F8600' }}>¥{fmtMoney(opt.data.__paid)}</b>
                      <span style={{ margin: '0 4px' }}>·</span>
                      <span style={{ color: 'var(--brand)', fontWeight: 600 }}>应收 ¥{fmtMoney(opt.data.__rec)}</span>
                    </div>
                  </div>
                )}
              />
            </Form.Item>
          </Col>

          {/* 分摊预览 */}
          {pickedOrders.length > 0 && (
            <Col span={24}>
              <div style={{
                background: 'linear-gradient(135deg, var(--brand-bg) 0%, #FFEFE6 100%)',
                border: '1px solid var(--brand-border)',
                borderRadius: 10,
                padding: 14,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#26201C' }}>
                    💰 分摊预览
                    <span style={{ marginLeft: 8, fontSize: 11, color: '#8c8c8c', fontWeight: 400 }}>
                      按各单应收比例自动分配，最后一单吸收误差
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: '#5F5E5A' }}>
                    本次 <b style={{ color: 'var(--brand)' }}>¥{fmtMoney(pay)}</b>
                    {payAmount > totalRec && <span style={{ marginLeft: 6, color: '#8c8c8c' }}>（超出应收 ¥{fmtMoney(payAmount - totalRec)} 不入订单）</span>}
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8 }}>
                  {allocations.map((a, i) => (
                    <div key={a.no} style={{
                      background: '#fff', border: '1px solid var(--brand-border)', borderRadius: 8, padding: '8px 10px',
                      display: 'flex', flexDirection: 'column', gap: 2,
                    }}>
                      <div style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 11, color: '#5F5E5A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.no}</div>
                      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: 10, color: '#bfbfbf' }}>应收 ¥{fmtMoney(a.rec)}</span>
                        <b style={{ fontSize: 13, color: '#3F8600' }}>+¥{fmtMoney(a.inc)}</b>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Col>
          )}

          <Col span={24}><Form.Item name="remark" label="备注" style={{ marginTop: 12 }}><Input.TextArea rows={2} placeholder="如：对公转账、银行流水号等" /></Form.Item></Col>
        </Row>
      </Form>
    </Modal>
  );
}

// ===== 开票记录弹窗 =====
function InvoiceModal({ open, customer, orders, onCancel, onDone }) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) form.setFieldsValue({ type: '增值税普票', status: '已开', date: dayjs(), order_nos: [] }); }, [open, form]);
  const submit = async () => {
    try {
      const v = await form.validateFields();
      setSaving(true);
      const type = v.type === '增值税专票' ? '专票' : (v.type === '电子普票' ? '电子普票' : '普票');
      const status = v.status === '未开' ? '待开' : (v.status === '已开' ? '已开' : v.status);
      const res = await apiFetch('/api/sale-invoice', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_id: customer.id, customer_name: v.title, tax_no: v.tax_no,
          invoice_type: type, amount_total: v.amount, tax: 0,
          order_nos: Array.isArray(v.order_nos) ? v.order_nos.join(',') : (v.order_nos || ''),
          invoice_date: v.date.format('YYYY-MM-DD'), status,
        }),
      }).then(r => r.json());
      setSaving(false);
      if (res.success) { message.success('开票已记录'); onDone(); } else message.error(res.error || '保存失败');
    } catch (e) { setSaving(false); if (e && e.errorFields) return; message.error('保存失败'); }
  };
  return (
    <Modal
      title={
        <Space size={8} align="center">
          <FileTextOutlined style={{ color: 'var(--brand)', fontSize: 18 }} />
          <span style={{ fontWeight: 600 }}>开票记录</span>
          <Tag color="default" style={{ marginLeft: 4, fontSize: 12 }}>{customer.name}</Tag>
        </Space>
      }
      open={open} onCancel={onCancel} onOk={submit} okText="保存" confirmLoading={saving} width={720} destroyOnClose
      okButtonProps={{ icon: <CheckCircleOutlined /> }}
    >
      <Form form={form} layout="vertical">
        {/* ===== 分组1：基础信息 ===== */}
        <div style={{ fontSize: 12, color: '#8c8c8c', fontWeight: 600, marginBottom: 8, letterSpacing: 0.5 }}>
          <FileTextOutlined style={{ marginRight: 6, color: 'var(--brand)' }} />基础信息
        </div>
        <div style={{ background: '#FAFAFA', borderRadius: 8, padding: '12px 16px', marginBottom: 16, border: '1px solid #F0F0F0' }}>
          <Row gutter={12}>
            <Col span={12}><Form.Item name="type" label="发票类型"><Select options={INVOICE_TYPES.map(t => ({ value: t, label: t }))} /></Form.Item></Col>
            <Col span={12}><Form.Item name="status" label="状态"><Select options={INVOICE_STATUS.map(s => ({ value: s, label: s }))} /></Form.Item></Col>
            <Col span={24}><Form.Item name="title" label="发票抬头" rules={[{ required: true, message: '必填' }]}><Input placeholder="公司全称" /></Form.Item></Col>
            <Col span={24}><Form.Item name="tax_no" label="税号"><Input placeholder="纳税人识别号（18-20 位）" style={{ fontFamily: 'ui-monospace, Menlo, monospace' }} /></Form.Item></Col>
          </Row>
        </div>

        {/* ===== 分组2：财务信息 ===== */}
        <div style={{ fontSize: 12, color: '#8c8c8c', fontWeight: 600, marginBottom: 8, letterSpacing: 0.5 }}>
          <DollarOutlined style={{ marginRight: 6, color: 'var(--brand)' }} />财务信息
        </div>
        <div style={{ background: '#FFF7F2', borderRadius: 8, padding: '12px 16px', marginBottom: 16, border: '1px solid #FFE0CC' }}>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="amount" label="开票金额（元）" rules={[{ required: true, message: '必填' }]}>
                <InputNumber
                  min={0} precision={2} style={{ width: '100%', fontSize: 16, fontWeight: 600 }}
                  formatter={value => `¥ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/¥\s?|,/g, '')}
                  placeholder="0.00"
                />
              </Form.Item>
            </Col>
            <Col span={12}><Form.Item name="date" label="开票日期"><DatePicker style={{ width: '100%' }} /></Form.Item></Col>
          </Row>
        </div>

        {/* ===== 分组3：关联订单 ===== */}
        <div style={{ fontSize: 12, color: '#8c8c8c', fontWeight: 600, marginBottom: 8, letterSpacing: 0.5 }}>
          <Tag style={{ marginRight: 6, margin: 0 }}>关联</Tag>关联订单
        </div>
        <div style={{ background: '#FAFAFA', borderRadius: 8, padding: '12px 16px', border: '1px solid #F0F0F0' }}>
          <Form.Item name="order_nos" label="选择该客户订单（可多选，关联订单会影响后续对账）" style={{ marginBottom: 0 }}>
            <Select
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="value"
              placeholder="选该客户订单（按订单号搜索）"
              popupMatchSelectWidth={false}
              listHeight={280}
              dropdownStyle={{ minWidth: 380, padding: 4 }}
              options={(orders || []).map(o => {
                const total = Number(o.order_price) || 0;
                const paid = Number(o.paid_amount) || 0;
                const rec = total - paid;
                return {
                  value: o.platform_order_no,
                  label: o.platform_order_no,
                  __order: o,
                  __total: total,
                  __paid: paid,
                  __rec: rec,
                };
              })}
              optionRender={(opt) => (
                <div style={{ padding: '4px 2px' }}>
                  <div style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12, fontWeight: 600, color: '#26201C' }}>{opt.data.value}</div>
                  <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 2 }}>
                    成交 <b style={{ color: '#5F5E5A' }}>¥{fmtMoney(opt.data.__total)}</b>
                    <span style={{ margin: '0 4px' }}>·</span>
                    应收 <span style={{ color: 'var(--brand)', fontWeight: 600 }}>¥{fmtMoney(opt.data.__rec)}</span>
                  </div>
                </div>
              )}
            />
          </Form.Item>
        </div>
      </Form>
    </Modal>
  );
}

// ===== 客户详情弹窗（居中 Modal，宽 920px）=====
function CustomerDetailModal({ customer, open, onClose, onChanged, prodOpts, allCustomers }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [custModal, setCustModal] = useState(false);
  const [orderModal, setOrderModal] = useState(false);
  const [followModal, setFollowModal] = useState(false);
  const [payModal, setPayModal] = useState(false);
  const [invModal, setInvModal] = useState(false);
  const [assignTarget, setAssignTarget] = useState(undefined); // 归属目标客户 id
  const [selectedKeys, setSelectedKeys] = useState([]);       // 勾选的待归属订单号
  const [assigning, setAssigning] = useState(false);
  const [shipOrder, setShipOrder] = useState(null);           // 待发货订单（弹发货窗）
  const [detailShip, setDetailShip] = useState(null);         // 详情弹窗内发起的分批发货（平级弹窗，避开 Modal 嵌套）
  const [costOrder, setCostOrder] = useState(null);           // 待修改运费/辅材的订单（弹修改窗）
  const [editOrder, setEditOrder] = useState(null);           // 待编辑的订单（弹完整编辑窗）

  const load = useCallback(() => {
    if (!customer) return;
    setLoading(true);
    apiFetch(`/api/crm/customers/${customer.id}`).then(r => r.json()).then(d => setDetail(d)).catch(() => message.error('详情加载失败')).finally(() => setLoading(false));
  }, [customer]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const c = detail?.customer || customer;
  const agg = detail?.agg || {};
  const isPending = c?.name === '待归属';

  const delFollowup = async (id) => {
    const res = await apiFetch(`/api/crm/followups/${id}`, { method: 'DELETE' }).then(r => r.json());
    if (res.success) { message.success('已删除'); load(); } else message.error(res.error || '删除失败');
  };
  const toggleFollowDone = async (id, done) => {
    const res = await apiFetch(`/api/crm/followups/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ done }) }).then(r => r.json());
    if (res.success) load();
  };
  const delPayment = async (id) => {
    const res = await apiFetch(`/api/crm/payments/${id}`, { method: 'DELETE' }).then(r => r.json());
    if (res.success) { message.success('已删除并回滚应收'); load(); onChanged(); } else message.error(res.error || '删除失败');
  };
  const delInvoice = async (id) => {
    const res = await apiFetch(`/api/sale-invoice/${id}`, { method: 'DELETE' }).then(r => r.json());
    if (res.success) { message.success('已删除'); load(); } else message.error(res.error || '删除失败');
  };
  const markInvoiced = async (id) => {
    const res = await apiFetch(`/api/sale-invoice/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status: '已开' }) }).then(r => r.json());
    if (res.success) load();
  };
  const doAssign = async () => {
    if (!assignTarget) { message.warning('请先选择目标客户'); return; }
    if (!selectedKeys.length) { message.warning('请先勾选要归属的订单'); return; }
    setAssigning(true);
    const res = await apiFetch('/api/crm/assign-orders', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ customer_id: assignTarget, order_nos: selectedKeys }),
    }).then(r => r.json());
    setAssigning(false);
    if (res.success) {
      message.success(`已归属 ${res.assigned} 单到客户档案`);
      setSelectedKeys([]); setAssignTarget(undefined); load(); onChanged();
    } else message.error(res.error || '归属失败');
  };

  const orderColumns = [
    { title: '订单号', dataIndex: 'platform_order_no', width: 170, render: v => <Text copyable style={{ fontSize: 12 }}>{v}</Text> },
    { title: '商品', dataIndex: 'product_name', ellipsis: true, render: v => <span style={{ fontSize: 12, color: '#8c8c8c' }}>{v || '-'}</span> },
    { title: '成交价', dataIndex: 'order_price', align: 'right', width: 90, render: v => fmtMoney(v) },
    { title: '已收', dataIndex: 'paid_amount', align: 'right', width: 80, render: v => <span style={{ color: '#3F8600' }}>{fmtMoney(v)}</span> },
    { title: '应收', dataIndex: 'receivable', align: 'right', width: 90, render: v => <span style={{ color: v > 0 ? '#D85A30' : '#3F8600', fontWeight: 600 }}>{fmtMoney(v)}</span> },
    { title: '利润', dataIndex: 'profit', align: 'right', width: 85, render: v => <span style={{ color: v < 0 ? '#CF1322' : '#3F8600' }}>{v > 0 ? '+' : ''}{fmtMoney(v)}</span> },
    { title: '发货', dataIndex: 'shipping_date', width: 100 },
  ];
  const payColumns = [
    { title: '日期', dataIndex: 'date', width: 100 },
    { title: '金额', dataIndex: 'amount', align: 'right', width: 100, render: v => <span style={{ color: '#3F8600', fontWeight: 600 }}>+{fmtMoney(v)}</span> },
    { title: '方式', dataIndex: 'method', width: 80 },
    { title: '关联订单', dataIndex: 'order_nos', render: (v, r) => {
        // 兼容：优先 order_nos 数组；旧数据用 order_no 单字符串
        const nos = Array.isArray(v) && v.length ? v : (r.order_no ? String(r.order_no).split(',').filter(Boolean) : []);
        if (!nos.length) return <span style={{ color: '#bfbfbf' }}>未关联</span>;
        return (
          <Space size={[4, 4]} wrap>
            {nos.map(n => (
              <Tag key={n} style={{
                fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 11, margin: 0,
                background: 'var(--brand-bg)', borderColor: 'var(--brand-border)', color: '#26201C',
              }}>{n}</Tag>
            ))}
          </Space>
        );
      } },
    { title: '备注', dataIndex: 'remark', ellipsis: true },
    { title: '操作', align: 'right', width: 70, render: (_, r) => <Popconfirm title="删除此回款并回滚各订单已收？" onConfirm={() => delPayment(r.id)}><Button size="small" type="text" danger icon={<DeleteOutlined />} /></Popconfirm> },
  ];
  // 注：开票 Tab 已从 Table 改为折叠面板（行内渲染，不再需要 invColumns）

  const tabItems = [
    {
      key: 'profile',
      label: '档案',
      children: (() => {
        // 累计成交 / 已回款 / 待跟进
        const orders = detail?.orders || [];
        const paidTotal = orders.reduce((s, o) => s + (Number(o.paid_amount) || 0), 0);
        const orderTotal = orders.reduce((s, o) => s + (Number(o.order_price) || 0), 0);
        const receivable = Math.max(0, orderTotal - paidTotal);
        const followups = detail?.followups || [];
        const pendingFollow = followups.filter(f => !f.done).length;
        // 建档天数
        let fileAge = 0;
        if (c?.created_at) {
          const t = new Date(String(c.created_at).replace(/-/g, '/')).getTime();
          if (!isNaN(t)) fileAge = Math.max(0, Math.floor((Date.now() - t) / 86400000));
        }
        const lvl = c?.level || '普通';
        const lvlColor = LEVEL_COLOR[lvl] || 'default';
        const InfoItem = ({ icon, label, value, color }) => (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '6px 0' }}>
            <span style={{
              width: 28, height: 28, borderRadius: 6, flexShrink: 0,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              background: color || '#FFF1EA', color: color ? '#fff' : 'var(--brand)', fontSize: 14,
            }}>{icon}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 11, color: '#8c8c8c', lineHeight: '16px' }}>{label}</div>
              <div style={{ fontSize: 13, color: '#26201C', marginTop: 2, wordBreak: 'break-all' }}>{value || '-'}</div>
            </div>
          </div>
        );
        const SectionCard = ({ icon, title, color, children }) => (
          <Card
            size="small"
            style={{ borderRadius: 8, marginBottom: 12, borderLeft: `3px solid ${color || 'var(--brand)'}` }}
            styles={{ body: { padding: '14px 18px' } }}
            title={
              <span style={{ fontSize: 13, fontWeight: 600, color: '#26201C' }}>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 22, height: 22, borderRadius: 5, marginRight: 8,
                  background: color || 'var(--brand)', color: '#fff', fontSize: 12,
                }}>{icon}</span>
                {title}
              </span>
            }
          >
            {children}
          </Card>
        );
        return (
          <div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
              <Button size="small" type="primary" icon={<EditOutlined />} onClick={() => setCustModal(true)}>编辑档案</Button>
            </div>
            {/* ===== 顶部 4 张摘要卡 ===== */}
            <Row gutter={12} style={{ marginBottom: 16 }}>
              <Col span={6}>
                <Card size="small" style={{ borderLeft: '3px solid var(--brand)', borderRadius: 6 }}>
                  <Statistic
                    title={<span style={{ fontSize: 12, color: '#8c8c8c' }}>累计成交额</span>}
                    value={orderTotal} precision={2} prefix="¥"
                    valueStyle={{ color: 'var(--brand)', fontWeight: 600, fontSize: 18 }}
                  />
                  <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 4 }}>
                    共 <b style={{ color: 'var(--brand)' }}>{orders.length}</b> 笔订单
                  </div>
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small" style={{ borderLeft: '3px solid #3F8600', borderRadius: 6 }}>
                  <Statistic
                    title={<span style={{ fontSize: 12, color: '#8c8c8c' }}>已回款</span>}
                    value={paidTotal} precision={2} prefix="¥"
                    valueStyle={{ color: '#3F8600', fontWeight: 600, fontSize: 18 }}
                  />
                  <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 4 }}>
                    回款率 <b style={{ color: '#3F8600' }}>{orderTotal > 0 ? Math.round(paidTotal / orderTotal * 100) : 0}</b>%
                  </div>
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small" style={{ borderLeft: `3px solid ${receivable > 0 ? '#FA8C16' : '#bfbfbf'}`, borderRadius: 6 }}>
                  <Statistic
                    title={<span style={{ fontSize: 12, color: '#8c8c8c' }}>待回款</span>}
                    value={receivable} precision={2} prefix="¥"
                    valueStyle={{ color: receivable > 0 ? '#FA8C16' : '#bfbfbf', fontWeight: 600, fontSize: 18 }}
                  />
                  <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 4 }}>
                    待跟进 <b style={{ color: pendingFollow ? '#FA8C16' : '#bfbfbf' }}>{pendingFollow}</b> 次
                  </div>
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small" style={{ borderLeft: '3px solid #1890FF', borderRadius: 6 }}>
                  <Statistic
                    title={<span style={{ fontSize: 12, color: '#8c8c8c' }}>合作天数</span>}
                    value={fileAge} suffix="天"
                    valueStyle={{ color: '#1890FF', fontWeight: 600, fontSize: 18 }}
                  />
                  <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 4 }}>
                    建档 {c?.created_at || '-'}
                  </div>
                </Card>
              </Col>
            </Row>

            {/* ===== 客户身份卡 ===== */}
            <SectionCard icon={<UserOutlined />} title="客户身份" color="var(--brand)">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <Tag color={lvlColor} style={{ fontSize: 13, padding: '2px 10px', borderRadius: 4 }}>
                  <b>{lvl}</b> 客户
                </Tag>
                {Array.isArray(c?.tags) && c.tags.length > 0 ? (
                  c.tags.map((t, i) => (
                    <Tag key={i} color="orange" style={{ borderRadius: 4 }}>{t}</Tag>
                  ))
                ) : (
                  <span style={{ fontSize: 12, color: '#bfbfbf' }}>暂无标签</span>
                )}
                {c?.source && (
                  <Tag color="blue" icon={<SmileOutlined />} style={{ borderRadius: 4 }}>来源：{c.source}</Tag>
                )}
              </div>
            </SectionCard>

            {/* ===== 基础联系卡 ===== */}
            <SectionCard icon={<PhoneOutlined />} title="联系方式" color="#1890FF">
              <Row gutter={16}>
                <Col span={12}>
                  <InfoItem icon={<UserOutlined />} label="联系人" value={c?.contact} color="#1890FF" />
                </Col>
                <Col span={12}>
                  <InfoItem icon={<PhoneOutlined />} label="电话" value={c?.phone} color="#1890FF" />
                </Col>
                <Col span={12}>
                  <InfoItem icon={<WechatOutlined />} label="微信" value={c?.wechat} color="#1890FF" />
                </Col>
                <Col span={12}>
                  <InfoItem icon={<GiftOutlined />} label="来源" value={c?.source} color="#1890FF" />
                </Col>
              </Row>
            </SectionCard>

            {/* ===== 位置信息卡 ===== */}
            <SectionCard icon={<EnvironmentOutlined />} title="位置与档案" color="#FA8C16">
              <Row gutter={16}>
                <Col span={12}>
                  <InfoItem icon={<EnvironmentOutlined />} label="所在区域" value={c?.region} color="#FA8C16" />
                </Col>
                <Col span={12}>
                  <InfoItem icon={<CalendarOutlined />} label="建档日期" value={c?.created_at} color="#FA8C16" />
                </Col>
                <Col span={24}>
                  <InfoItem icon={<EnvironmentOutlined />} label="详细地址" value={c?.address} color="#FA8C16" />
                </Col>
              </Row>
            </SectionCard>

            {/* ===== 备注卡 ===== */}
            <SectionCard icon={<FileTextOutlined />} title="备注" color="#8c8c8c">
              <div style={{
                fontSize: 13, color: c?.remark ? '#26201C' : '#bfbfbf',
                lineHeight: '22px', whiteSpace: 'pre-wrap', minHeight: 24,
                padding: '8px 12px', background: '#FAFAFA', borderRadius: 6,
              }}>
                {c?.remark || '暂无备注'}
              </div>
            </SectionCard>
          </div>
        );
      })(),
    },
    {
      key: 'follow',
      label: `跟进${(detail?.followups?.length || 0) ? ` ${detail.followups.length}` : ''}`,
      children: (() => {
        const fList = detail?.followups || [];
        const doneCount = fList.filter(f => f.done).length;
        const pendingCount = fList.length - doneCount;
        return (
          <div>
            {/* ===== 顶部摘要条 ===== */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '12px 16px', marginBottom: 16, borderRadius: 8,
              background: '#FFF9F6', border: '1px solid #FFE4D6',
            }}>
              <Space size={20}>
                <span style={{ fontSize: 13, color: '#8c8c8c' }}>
                  共 <b style={{ color: 'var(--brand)', fontSize: 16 }}>{fList.length}</b> 次跟进
                </span>
                <span style={{ fontSize: 13, color: '#8c8c8c' }}>
                  <CheckCircleOutlined style={{ color: '#3F8600', marginRight: 4 }} />
                  已完成 <b style={{ color: '#3F8600', fontSize: 16 }}>{doneCount}</b>
                </span>
                <span style={{ fontSize: 13, color: '#8c8c8c' }}>
                  <ClockCircleOutlined style={{ color: '#FA8C16', marginRight: 4 }} />
                  待跟进 <b style={{ color: '#FA8C16', fontSize: 16 }}>{pendingCount}</b>
                </span>
              </Space>
              <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => setFollowModal(true)}>新增跟进</Button>
            </div>

            {!fList.length ? (
              <Empty description="暂无跟进记录，点击右上角「新增跟进」开始记录" style={{ marginTop: 40 }} />
            ) : (
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                {fList.map(f => {
                  const isDone = f.done;
                  const accentColor = isDone ? '#3F8600' : '#FA8C16';
                  const accentBg = isDone ? '#F6FFED' : '#FFF7E6';
                  return (
                    <Card
                      key={f.id}
                      size="small"
                      hoverable
                      style={{
                        borderRadius: 8,
                        borderLeft: `3px solid ${accentColor}`,
                        background: '#fff',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                      }}
                      styles={{ body: { padding: '14px 18px' } }}
                    >
                      {/* 头部：时间 + 渠道 + 状态 */}
                      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
                        <CalendarOutlined style={{ color: accentColor }} />
                        <span style={{ fontSize: 14, fontWeight: 600, color: '#26201C' }}>{f.time}</span>
                        <Tag color="blue" icon={<MessageOutlined />} style={{ borderRadius: 4 }}>{f.channel || '其他'}</Tag>
                        {isDone
                          ? <Tag color="green" icon={<CheckCircleOutlined />} style={{ borderRadius: 4 }}>已完成</Tag>
                          : <Tag color="orange" icon={<ClockCircleOutlined />} style={{ borderRadius: 4 }}>待跟进</Tag>
                        }
                        {f.next_follow_at && (
                          <Tag color="gold" icon={<CalendarOutlined />} style={{ borderRadius: 4 }}>
                            下次：{f.next_follow_at}
                          </Tag>
                        )}
                        <div style={{ marginLeft: 'auto' }}>
                          {!f.done && (
                            <Button size="small" type="link" onClick={() => toggleFollowDone(f.id, true)}>
                              标记完成
                            </Button>
                          )}
                          <Popconfirm title="删除此跟进？" onConfirm={() => delFollowup(f.id)}>
                            <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                          </Popconfirm>
                        </div>
                      </div>

                      {/* 内容 */}
                      <div style={{
                        fontSize: 13, color: '#434343', lineHeight: '22px',
                        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                        padding: '8px 12px', background: accentBg, borderRadius: 6,
                      }}>
                        {f.content}
                      </div>
                    </Card>
                  );
                })}
              </Space>
            )}
          </div>
        );
      })(),
    },
    {
      key: 'orders',
      label: `成交${detail?.orders?.length ? ` ${detail.orders.length}` : ''}`,
      children: (
        <div>
          {isPending ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap', padding: 10, background: '#FFF9F6', borderRadius: 8 }}>
                <span style={{ fontSize: 12, color: '#8c8c8c' }}>勾选订单归属到：</span>
                <Select
                  style={{ minWidth: 220 }}
                  placeholder="选择目标客户"
                  value={assignTarget}
                  onChange={setAssignTarget}
                  showSearch
                  optionFilterProp="label"
                  options={(allCustomers || []).filter(x => x.name !== '待归属').map(x => ({ value: x.id, label: x.name }))}
                />
                <Button size="small" type="primary" loading={assigning} onClick={doAssign}>
                  确认归属 {selectedKeys.length ? `(${selectedKeys.length})` : ''}
                </Button>
              </div>
              <Table size="small" rowKey="platform_order_no" columns={orderColumns} dataSource={detail?.orders || []}
                rowSelection={{ selectedRowKeys: selectedKeys, onChange: setSelectedKeys }}
                pagination={{ pageSize: 10, showTotal: t => `共 ${t} 单` }} scroll={{ x: 720 }}
                locale={{ emptyText: '历史微信订单待归属，勾选后选择目标客户批量归属' }} />
            </>
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => setOrderModal(true)}>新增成交</Button>
              </div>
              {(!detail?.orders || !detail.orders.length) ? (
                <Empty description="暂无成交订单" />
              ) : (() => {
                const orders = detail.orders || [];
                const sumTotal = orders.reduce((s, o) => s + (Number(o.order_price) || 0), 0);
                const sumPaid = orders.reduce((s, o) => s + (Number(o.paid_amount) || 0), 0);
                const sumProfit = orders.reduce((s, o) => s + (Number(o.profit) || 0), 0);
                const sumRecv = sumTotal - sumPaid;
                return (
                  <>
                    {/* 顶部 4 指标卡 */}
                    <Row gutter={[12, 12]} style={{ marginBottom: 14 }}>
                      <Col span={6}>
                        <div style={{ background: '#FFF9F6', borderRadius: 8, padding: '12px 16px', border: '1px solid #FFE6D6' }}>
                          <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>成交总额</div>
                          <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--brand)', fontFamily: 'ui-monospace, Menlo, monospace' }}>¥{fmtMoney(sumTotal)}</div>
                          <div style={{ fontSize: 11, color: '#bfbfbf', marginTop: 2 }}>{orders.length} 单</div>
                        </div>
                      </Col>
                      <Col span={6}>
                        <div style={{ background: '#F6FFED', borderRadius: 8, padding: '12px 16px', border: '1px solid #D9F7BE' }}>
                          <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>已收</div>
                          <div style={{ fontSize: 22, fontWeight: 700, color: '#3F8600', fontFamily: 'ui-monospace, Menlo, monospace' }}>¥{fmtMoney(sumPaid)}</div>
                          <div style={{ fontSize: 11, color: '#bfbfbf', marginTop: 2 }}>回款率 {sumTotal > 0 ? Math.round(sumPaid / sumTotal * 100) : 0}%</div>
                        </div>
                      </Col>
                      <Col span={6}>
                        <div style={{ background: sumRecv > 0 ? '#FFF1F0' : '#F6FFED', borderRadius: 8, padding: '12px 16px', border: '1px solid ' + (sumRecv > 0 ? '#FFCCC7' : '#D9F7BE') }}>
                          <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>应收余额</div>
                          <div style={{ fontSize: 22, fontWeight: 700, color: sumRecv > 0 ? '#CF1322' : '#3F8600', fontFamily: 'ui-monospace, Menlo, monospace' }}>¥{fmtMoney(sumRecv)}</div>
                          <div style={{ fontSize: 11, color: '#bfbfbf', marginTop: 2 }}>{sumRecv > 0 ? '待回款' : '已结清'}</div>
                        </div>
                      </Col>
                      <Col span={6}>
                        <div style={{ background: sumProfit >= 0 ? '#F6FFED' : '#FFF1F0', borderRadius: 8, padding: '12px 16px', border: '1px solid ' + (sumProfit >= 0 ? '#D9F7BE' : '#FFCCC7') }}>
                          <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>总利润</div>
                          <div style={{ fontSize: 22, fontWeight: 700, color: sumProfit >= 0 ? '#3F8600' : '#CF1322', fontFamily: 'ui-monospace, Menlo, monospace' }}>¥{fmtMoney(sumProfit)}</div>
                          <div style={{ fontSize: 11, color: '#bfbfbf', marginTop: 2 }}>毛利率 {sumTotal > 0 ? Math.round(sumProfit / sumTotal * 100) : 0}%</div>
                        </div>
                      </Col>
                    </Row>

                    {/* 每单一张卡片 */}
                    {orders.map(o => {
                      const receivable = (Number(o.order_price) || 0) - (Number(o.paid_amount) || 0);
                      const cost = (Number(o.product_cost) || 0) + (Number(o.shipping_fee) || 0) + (Number(o.packaging_cost) || 0);
                      const items = Array.isArray(o.spec_items) && o.spec_items.length ? o.spec_items : [];
                      const profitVal = Number(o.profit) || 0;
                      const profitColor = profitVal >= 0 ? '#3F8600' : '#CF1322';
                      const margin = (Number(o.order_price) || 0) > 0 ? profitVal / (Number(o.order_price) || 0) * 100 : 0;
                      return (
                        <Card
                          key={o.platform_order_no}
                          size="small"
                          hoverable
                          onClick={() => setEditOrder(o)}
                          style={{ marginBottom: 12, borderRadius: 10, border: receivable > 0 ? '1px solid #FFCCC7' : '1px solid #E8E8E8', cursor: 'pointer' }}
                          styles={{ body: { padding: 16 } }}
                        >
                          {/* 卡片头 */}
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <Space size={8} wrap>
                                <span style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 14, fontWeight: 700, color: '#26201C' }}>{o.platform_order_no}</span>
                                <Tag color={shipStatusTag(o).color}>{shipStatusTag(o).text}</Tag>
                                {receivable > 0 ? <Tag color="orange">待回款</Tag> : <Tag color="green">已结清</Tag>}
                                {o.is_dropship && <Tag color="cyan">整单代发</Tag>}
                                {!o.is_dropship && (o.spec_items || []).some(it => it.is_dropship) && <Tag color="cyan">含代发</Tag>}
                              </Space>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--brand)', fontFamily: 'ui-monospace, Menlo, monospace' }}>¥{fmtMoney(o.order_price)}</div>
                              <div style={{ fontSize: 12, color: profitColor, fontWeight: 600, marginTop: 2 }}>
                                利润 {profitVal >= 0 ? '+' : ''}¥{fmtMoney(o.profit)}
                              </div>
                              <div style={{ fontSize: 11, color: profitColor, fontWeight: 600, marginTop: 1 }}>
                                毛利率 {margin.toFixed(1)}%
                              </div>
                              {shipProgressOf(o).status !== 'shipped' && (
                                <Button size="small" type="primary" style={{ marginTop: 6 }} onClick={(e) => { e.stopPropagation(); setShipOrder(o); }}>
                                  {shipProgressOf(o).status === 'partial' ? '发剩余' : '发货'}
                                </Button>
                              )}
                            </div>
                          </div>

                          {/* 商品明细：多商品表格 / 单商品单行 */}
                          {items.length > 1 ? (
                            <div style={{ marginBottom: 12, border: '1px solid #F0F0F0', borderRadius: 6, overflow: 'hidden' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                <thead>
                                  <tr style={{ background: '#FAFAFA' }}>
                                    <th style={{ padding: '6px 8px', fontWeight: 600, color: '#8c8c8c', textAlign: 'center', width: 32 }}>#</th>
                                    <th style={{ padding: '6px 8px', fontWeight: 600, color: '#8c8c8c', textAlign: 'left' }}>编码</th>
                                    <th style={{ padding: '6px 8px', fontWeight: 600, color: '#8c8c8c', textAlign: 'left' }}>商品名</th>
                                    <th style={{ padding: '6px 8px', fontWeight: 600, color: '#8c8c8c', textAlign: 'center', width: 56 }}>件数</th>
                                    <th style={{ padding: '6px 8px', fontWeight: 600, color: '#8c8c8c', textAlign: 'center', width: 72 }}>实发</th>
                                    <th style={{ padding: '6px 8px', fontWeight: 600, color: '#8c8c8c', textAlign: 'left', width: 130 }}>发货</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {items.map((it, i) => (
                                    <tr key={i} style={{ borderTop: '1px solid #F0F0F0' }}>
                                      <td style={{ padding: '6px 8px', textAlign: 'center', color: '#bfbfbf' }}>{i + 1}</td>
                                      <td style={{ padding: '6px 8px', fontFamily: 'ui-monospace, Menlo, monospace', color: '#2F54EB' }}>{it.spec_code}</td>
                                      <td style={{ padding: '6px 8px', color: '#26201C' }}>{it.product_name || '-'}</td>
                                      <td style={{ padding: '6px 8px', textAlign: 'center', color: '#26201C' }}>{it.piece_qty}</td>
                                      <td style={{ padding: '6px 8px', textAlign: 'center', color: '#26201C' }}>{it.product_quantity}</td>
                                      <td style={{ padding: '6px 8px' }}>
                                        {it.shipped ? (
                                          <span style={{ fontSize: 11, color: '#3F8600' }}>
                                            已发 {it.ship_date ? it.ship_date.slice(5) : ''}
                                            {it.tracking_no ? <span style={{ color: '#bfbfbf' }}> · {it.tracking_no}</span> : null}
                                          </span>
                                        ) : (
                                          <span style={{ fontSize: 11, color: '#D85A30' }}>待发货</span>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : items.length === 1 ? (
                            <div style={{ fontSize: 13, color: '#26201C', marginBottom: 12, fontWeight: 500 }}>
                              <Tag color="geekblue" style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12, margin: 0 }}>{items[0].spec_code}</Tag>
                              <span style={{ marginLeft: 8 }}>{items[0].product_name || o.product_name || '-'}</span>
                              <span style={{ color: '#bfbfbf', marginLeft: 6 }}>× {items[0].piece_qty} 件</span>
                              <span style={{ color: '#bfbfbf', marginLeft: 6 }}>实发 {items[0].product_quantity}</span>
                            </div>
                          ) : (
                            <div style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 12, fontWeight: 500 }}>
                              {o.product_name || '-'}
                              {o.piece_qty ? <span style={{ color: '#bfbfbf', marginLeft: 6 }}>× {o.piece_qty}</span> : null}
                            </div>
                          )}

                          {/* 财务摘要 4 段 */}
                          <Row gutter={0} style={{ background: '#FAFAFA', borderRadius: 6, padding: '10px 0', marginBottom: 12 }}>
                            <Col span={6} style={{ textAlign: 'center', borderRight: '1px solid #E8E8E8' }}>
                              <div style={{ fontSize: 11, color: '#8c8c8c' }}>成交价</div>
                              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--brand)', fontFamily: 'ui-monospace, Menlo, monospace', marginTop: 2 }}>¥{fmtMoney(o.order_price)}</div>
                            </Col>
                            <Col span={6} style={{ textAlign: 'center', borderRight: '1px solid #E8E8E8' }}>
                              <div style={{ fontSize: 11, color: '#8c8c8c' }}>已收</div>
                              <div style={{ fontSize: 15, fontWeight: 600, color: '#3F8600', fontFamily: 'ui-monospace, Menlo, monospace', marginTop: 2 }}>¥{fmtMoney(o.paid_amount)}</div>
                            </Col>
                            <Col span={6} style={{ textAlign: 'center', borderRight: '1px solid #E8E8E8' }}>
                              <div style={{ fontSize: 11, color: '#8c8c8c' }}>应收</div>
                              <div style={{ fontSize: 15, fontWeight: 700, color: receivable > 0 ? '#CF1322' : '#3F8600', fontFamily: 'ui-monospace, Menlo, monospace', marginTop: 2 }}>¥{fmtMoney(receivable)}</div>
                            </Col>
                            <Col span={6} style={{ textAlign: 'center' }}>
                              <div style={{ fontSize: 11, color: '#8c8c8c' }}>利润</div>
                              <div style={{ fontSize: 15, fontWeight: 700, color: (Number(o.profit) || 0) >= 0 ? '#3F8600' : '#CF1322', fontFamily: 'ui-monospace, Menlo, monospace', marginTop: 2 }}>{Number(o.profit) >= 0 ? '+' : ''}¥{fmtMoney(o.profit)}</div>
                            </Col>
                          </Row>

                          {/* 物流信息 */}
                          <div style={{ fontSize: 12, color: '#595959', marginBottom: 8, lineHeight: 1.7 }}>
                            <Space size={14} wrap>
                              {o.shipping_date && <span>📅 {o.shipping_date}</span>}
                              {(o.region_view || o.order_region) && <span>📍 {o.region_view || o.order_region}</span>}
                              {o.courier_company && <span>🚚 {o.courier_company}</span>}
                              {o.tracking_no && <span style={{ fontFamily: 'ui-monospace, Menlo, monospace' }}>单号 {o.tracking_no}</span>}
                            </Space>
                          </div>

                          {/* 客户与地址 */}
                          {(o.customer_name || o.address) && (
                            <div style={{ fontSize: 12, color: '#595959', marginBottom: 8 }}>
                              {o.customer_name && <span style={{ marginRight: 8 }}><b>客户：</b>{o.customer_name}</span>}
                              {o.address && <span style={{ color: '#8c8c8c' }}>📍 {o.address}</span>}
                            </div>
                          )}

                          {/* 成本明细 */}
                          <div style={{ fontSize: 11, color: '#8c8c8c', borderTop: '1px dashed #E8E8E8', paddingTop: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 4 }}>
                            <span style={{ lineHeight: 1.8 }}>
                              成本：商品 ¥{fmtMoney(o.product_cost)} + 运费 ¥{fmtMoney(o.shipping_fee)} + 辅材 ¥{fmtMoney(o.packaging_cost)} = <b style={{ color: '#595959' }}>¥{fmtMoney(cost)}</b>
                              {(o.manual_shipping != null || o.manual_packaging != null) && <Tag color="orange" style={{ marginLeft: 8, fontSize: 10, lineHeight: '16px' }}>手动</Tag>}
                              {o.remark ? <span style={{ marginLeft: 12 }}>· 备注：{o.remark}</span> : null}
                            </span>
                            <Space size={4}>
                              <Button size="small" type="primary" ghost icon={<EditOutlined />} onClick={(e) => { e.stopPropagation(); setEditOrder(o); }}>编辑</Button>
                              <Button size="small" type="link" icon={<EditOutlined />} onClick={(e) => { e.stopPropagation(); setCostOrder(o); }}>改运费/辅材</Button>
                            </Space>
                          </div>
                        </Card>
                      );
                    })}
                  </>
                );
              })()}
            </>
          )}
        </div>
      ),
    },
    {
      key: 'receivable',
      label: '应收/回款',
      children: (
        <div>
          <Row gutter={16} style={{ marginBottom: 12 }}>
            <Col span={6}><Card size="small"><Statistic title="成交总额" value={agg.total_amount || 0} precision={2} prefix="¥" /></Card></Col>
            <Col span={6}><Card size="small"><Statistic title="已收" value={agg.total_paid || 0} precision={2} prefix="¥" valueStyle={{ color: '#3F8600' }} /></Card></Col>
            <Col span={6}><Card size="small"><Statistic title="应收余额" value={agg.receivable || 0} precision={2} prefix="¥" valueStyle={{ color: (agg.receivable || 0) > 0 ? '#D85A30' : '#3F8600', fontWeight: 600 }} /></Card></Col>
            <Col span={6} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
              <Button size="small" type="primary" icon={<DollarOutlined />} onClick={() => setPayModal(true)}>登记回款</Button>
            </Col>
          </Row>
          <div style={{ fontSize: 13, fontWeight: 600, margin: '8px 0' }}>回款流水</div>
          <Table size="small" rowKey="id" columns={payColumns} dataSource={detail?.payments || []} pagination={false} locale={{ emptyText: '暂无回款记录' }} />
        </div>
      ),
    },
    {
      key: 'invoice',
      label: `开票${detail?.invoices?.length ? ` ${detail.invoices.length}` : ''}`,
      children: (() => {
        const invList = detail?.invoices || [];
        const invTotal = invList.reduce((s, inv) => s + (Number(inv.amount) || 0), 0);
        const invInvoiced = invList.filter(inv => inv.status === '已开').reduce((s, inv) => s + (Number(inv.amount) || 0), 0);
        const invPending = invTotal - invInvoiced;
        return (
          <div>
            {/* ===== 顶部汇总卡片 ===== */}
            <Row gutter={12} style={{ marginBottom: 16 }}>
              <Col span={6}>
                <Card size="small" style={{ borderLeft: '3px solid var(--brand)', borderRadius: 6 }}>
                  <Statistic
                    title={<span style={{ fontSize: 12, color: '#8c8c8c' }}>开票总额</span>}
                    value={invTotal} precision={2} prefix="¥"
                    valueStyle={{ color: 'var(--brand)', fontWeight: 600, fontSize: 18 }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small" style={{ borderLeft: '3px solid #3F8600', borderRadius: 6 }}>
                  <Statistic
                    title={<span style={{ fontSize: 12, color: '#8c8c8c' }}>已开金额</span>}
                    value={invInvoiced} precision={2} prefix="¥"
                    valueStyle={{ color: '#3F8600', fontWeight: 600, fontSize: 18 }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small" style={{ borderLeft: `3px solid ${invPending > 0 ? '#FA8C16' : '#bfbfbf'}`, borderRadius: 6 }}>
                  <Statistic
                    title={<span style={{ fontSize: 12, color: '#8c8c8c' }}>待开金额</span>}
                    value={invPending} precision={2} prefix="¥"
                    valueStyle={{ color: invPending > 0 ? '#FA8C16' : '#bfbfbf', fontWeight: 600, fontSize: 18 }}
                  />
                </Card>
              </Col>
              <Col span={6} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => setInvModal(true)}>新增开票</Button>
              </Col>
            </Row>

            {/* ===== 发票卡片列表 ===== */}
            {!invList.length ? (
              <Empty description="暂无开票记录，点击右上角「新增开票」开始记录" style={{ marginTop: 40 }} />
            ) : (
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                {invList.map(inv => {
                  const orderNos = String(inv.order_nos || '').split(/[,，、\s]+/).filter(Boolean);
                  const isInvoiced = inv.status === '已开';
                  return (
                    <Card
                      key={inv.id}
                      size="small"
                      hoverable
                      style={{
                        borderRadius: 8,
                        borderLeft: `3px solid ${isInvoiced ? '#3F8600' : '#FA8C16'}`,
                        background: '#fff',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                      }}
                      styles={{ body: { padding: 16 } }}
                    >
                      {/* 卡片头部：抬头 + 日期 + 类型 + 状态 + 操作 */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                        <div style={{ flex: 1 }}>
                          <Space size={8} align="center" wrap>
                            <FileTextOutlined style={{ color: 'var(--brand)', fontSize: 14 }} />
                            <span style={{ fontWeight: 600, fontSize: 15, color: '#26201C' }}>{inv.title || '未填抬头'}</span>
                            <Tag color="blue" style={{ margin: 0 }}>{inv.type}</Tag>
                            <Tag
                              color={isInvoiced ? 'green' : 'orange'}
                              icon={isInvoiced ? <CheckCircleOutlined /> : <ClockCircleOutlined />}
                              style={{ margin: 0 }}
                            >
                              {inv.status}
                            </Tag>
                          </Space>
                          <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 6 }}>📅 开票日期：{inv.date}</div>
                        </div>
                        <Space size={4} onClick={e => e.stopPropagation()}>
                          {inv.file_url && (
                            <a href={inv.file_url} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: 'var(--brand)' }}>
                              <PaperClipOutlined /> 票面
                            </a>
                          )}
                          {!isInvoiced && (
                            <Button size="small" type="link" icon={<CheckCircleOutlined />} onClick={() => markInvoiced(inv.id)}>
                              标记已开
                            </Button>
                          )}
                          <Popconfirm title="删除此开票记录？" onConfirm={() => delInvoice(inv.id)} okText="确认删除" cancelText="取消">
                            <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                          </Popconfirm>
                        </Space>
                      </div>

                      {/* 卡片主体：3 段摘要（金额 / 税号 / 关联订单） */}
                      <Row gutter={0} style={{ background: '#FAFAFA', borderRadius: 6, padding: '10px 0' }}>
                        <Col span={6} style={{ textAlign: 'center', borderRight: '1px solid #E8E8E8' }}>
                          <div style={{ fontSize: 11, color: '#8c8c8c' }}>开票金额</div>
                          <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--brand)', fontFamily: 'ui-monospace, Menlo, monospace', marginTop: 2 }}>
                            ¥{fmtMoney(inv.amount)}
                          </div>
                        </Col>
                        <Col span={10} style={{ textAlign: 'center', borderRight: '1px solid #E8E8E8', padding: '0 8px' }}>
                          <div style={{ fontSize: 11, color: '#8c8c8c' }}>税号</div>
                          <div style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 13, color: '#26201C', marginTop: 2 }}>
                            {inv.tax_no || '-'}
                          </div>
                        </Col>
                        <Col span={8} style={{ textAlign: 'center', padding: '0 8px' }}>
                          <div style={{ fontSize: 11, color: '#8c8c8c' }}>关联订单</div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: orderNos.length ? '#D85A30' : '#bfbfbf', marginTop: 2 }}>
                            {orderNos.length ? `${orderNos.length} 单` : '未关联'}
                          </div>
                        </Col>
                      </Row>

                      {/* 关联订单 chip 列表 */}
                      {orderNos.length > 0 && (
                        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px dashed #F0F0F0' }}>
                          <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 6 }}>关联订单号</div>
                          <Space size={[6, 6]} wrap>
                            {orderNos.map((no, i) => (
                              <Tag key={i} color="geekblue" style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12, margin: 0 }}>
                                {no}
                              </Tag>
                            ))}
                          </Space>
                        </div>
                      )}
                    </Card>
                  );
                })}
              </Space>
            )}
          </div>
        );
      })(),
    },
  ];

  return (
    <>
      <Modal
        title={
          <Space size={8} wrap>
            <b style={{ fontSize: 16 }}>{c?.name}</b>
            {c?.level && <Tag color={LEVEL_COLOR[c.level] || 'default'}>{c.level}</Tag>}
            {(c?.tags || []).map(t => <Tag key={t}>{t}</Tag>)}
          </Space>
        }
        width={960}
        open={open}
        onCancel={onClose}
        footer={<Button onClick={onClose}>关闭</Button>}
        destroyOnClose
        centered
        styles={{ body: { maxHeight: '80vh', overflow: 'auto', padding: '16px 24px' } }}
        style={{ top: 20 }}
      >
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center' }}>加载中...</div>
        ) : (
          <Collapse
            items={tabItems}
            defaultActiveKey={['profile']}
            bordered={false}
            expandIconPosition="end"
            style={{ background: 'transparent' }}
          />
        )}
      </Modal>

      {custModal && <CustomerModal open={custModal} customer={c} onCancel={() => setCustModal(false)} onDone={() => { setCustModal(false); load(); onChanged(); }} />}
      {orderModal && <OrderModal open={orderModal} customer={c} prodOpts={prodOpts} onCancel={() => setOrderModal(false)} onDone={() => { setOrderModal(false); load(); onChanged(); }} />}
      {followModal && <FollowupModal open={followModal} customer={c} onCancel={() => setFollowModal(false)} onDone={() => { setFollowModal(false); load(); onChanged(); }} />}
      {payModal && <PaymentModal open={payModal} customer={c} orders={detail?.orders || []} onCancel={() => setPayModal(false)} onDone={() => { setPayModal(false); load(); onChanged(); }} />}
      {invModal && <InvoiceModal open={invModal} customer={c} orders={detail?.orders || []} onCancel={() => setInvModal(false)} onDone={() => { setInvModal(false); load(); }} />}
      {shipOrder && <ShipModal open={!!shipOrder} order={shipOrder} onCancel={() => setShipOrder(null)} onDone={() => { setShipOrder(null); load(); onChanged(); }} />}
      {/* 详情弹窗内发起的分批发货：平级渲染（发货后拉最新订单更新详情弹窗数据，保持详情打开） */}
      {detailShip && <ShipModal open={!!detailShip} order={detailShip} zIndex={2000} onCancel={() => setDetailShip(null)}
        onDone={async () => {
          setDetailShip(null);
          try {
            const res = await apiFetch(`/api/orders/detail?platform_order_no=${encodeURIComponent(detailShip.platform_order_no)}`).then(r => r.json());
            if (res.success && res.data) setEditOrder(res.data);
          } catch (e) { /* 静默 */ }
          load(); onChanged();
        }} />}
      {costOrder && <CostEditModal open={!!costOrder} order={costOrder} onCancel={() => setCostOrder(null)} onDone={() => { setCostOrder(null); load(); onChanged(); }} />}
      {editOrder && <EditOrderModal open={!!editOrder} order={editOrder} prodOpts={prodOpts} allCustomers={allCustomers} onCancel={() => setEditOrder(null)} onDone={() => { setEditOrder(null); load(); onChanged(); }} onChanged={() => { load(); onChanged(); }} onShip={(o) => setDetailShip(o)} />}
    </>
  );
}

// ===== 主页面 =====
export default function Crm() {
  const [customers, setCustomers] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [custModal, setCustModal] = useState(false);
  const [editCust, setEditCust] = useState(null);
  const [drawerCust, setDrawerCust] = useState(null);
  const [prodOpts, setProdOpts] = useState([]);
  const [quickOrder, setQuickOrder] = useState(false);
  const [recentOrders, setRecentOrders] = useState([]);
  const [recentTotal, setRecentTotal] = useState(0);
  const [recentPage, setRecentPage] = useState(1);
  const [recentLoading, setRecentLoading] = useState(false);
  const [orderFilter, setOrderFilter] = useState('all'); // 订单状态筛选：all / unshipped / unpaid / settled
  const [orderCounts, setOrderCounts] = useState(null);
  const [orderDetail, setOrderDetail] = useState(null); // 订单Tab点订单号 → 直达订单详情弹窗
  const [detailShip, setDetailShip] = useState(null);   // 详情弹窗内发起的分批发货（平级弹窗，避开 Modal 嵌套）
  const [activeTab, setActiveTab] = useState('customers'); // 主页面分块：customers / orders / top

  const loadCustomers = useCallback(() => {
    setLoading(true);
    const q = keyword ? `?keyword=${encodeURIComponent(keyword)}` : '';
    apiFetch(`/api/crm/customers${q}`).then(r => r.json()).then(d => setCustomers(d.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, [keyword]);
  const loadStats = useCallback(() => {
    apiFetch('/api/crm/stats').then(r => r.json()).then(setStats).catch(() => {});
  }, []);
  const loadProdOpts = useCallback(() => {
    apiFetch('/api/products').then(r => r.json()).then(d => {
      const set = {};
      for (const p of (d.data || [])) {
        const list = p.children && p.children.length ? p.children : (p.spec_code ? [p] : []);
        for (const c of list) { const raw = String(c.spec_code || '').trim(); if (raw && !set[raw]) set[raw] = p.product_name || raw; }
      }
      setProdOpts(Object.entries(set).map(([code, name]) => ({ value: code, label: `${code}（${name}）` })));
    }).catch(() => {});
  }, []);
  const loadRecentOrders = useCallback((page = 1, status = 'all') => {
    setRecentLoading(true);
    apiFetch(`/api/crm/recent-orders?limit=10&page=${page}&status=${status}`).then(r => r.json()).then(d => {
      setRecentOrders(d.data || []);
      setRecentTotal(d.total || 0);
      if (d.counts) setOrderCounts(d.counts);
    }).catch(() => {}).finally(() => setRecentLoading(false));
  }, []);

  useEffect(() => { loadCustomers(); }, [loadCustomers]);
  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadProdOpts(); }, [loadProdOpts]);
  useEffect(() => { loadRecentOrders(recentPage, orderFilter); }, [loadRecentOrders, recentPage, orderFilter]);

  const refresh = () => { loadCustomers(); loadStats(); loadRecentOrders(recentPage); };

  const delCustomer = async (c) => {
    const res = await apiFetch(`/api/crm/customers/${c.id}`, { method: 'DELETE' }).then(r => r.json());
    if (res.success) { message.success('已删除'); refresh(); } else message.error(res.error || '删除失败');
  };

  // 下单日期：从 WX-YYYYMMDD-NNNN / YYMMDD-XXXXX 单号前缀解析（MM-DD 展示），解析失败回退发货日期
  const orderDateOf = (o) => {
    const no = String(o.platform_order_no || '');
    let m = no.match(/^WX-(\d{4})(\d{2})(\d{2})-/);
    if (m) return `${m[2]}-${m[3]}`;
    m = no.match(/^(\d{2})(\d{2})(\d{2})-\d+/);
    if (m) return `20${m[1]}-${m[2]}-${m[3]}`.slice(5);
    return o.shipping_date ? String(o.shipping_date).slice(5) : '';
  };
  // 状态分类：部分已发 > 待发货 > 待回款（与后端 counts 口径一致）
  const orderClassOf = (o) => {
    const p = shipProgressOf(o);
    if (p.status === 'partial') return 'partial';
    if (!o.shipping_date) return 'unshipped';
    return (Number(o.receivable) || 0) > 0.005 ? 'unpaid' : 'settled';
  };
  const ORDER_PILL = {
    unshipped: { bg: '#FAEEDA', color: '#854F0B', text: '待发货' },
    partial: { bg: '#F5C4B3', color: '#993C1D', text: '部分已发' },
    unpaid: { bg: '#FAECE7', color: '#993C1D', text: '待回款' },
    settled: null, // 已发货显示灰色胶囊 + 日期
  };
  const orderPillOf = (o) => {
    const cls = orderClassOf(o);
    if (cls === 'settled') {
      return <span style={{ background: '#F1EFE8', color: '#5F5E5A', borderRadius: 10, padding: '2px 10px', fontSize: 11, fontWeight: 600, display: 'inline-block' }}>已发货 {String(o.shipping_date).slice(5)}</span>;
    }
    if (cls === 'partial') {
      const pr = shipProgressOf(o);
      return <span style={{ background: '#F5C4B3', color: '#993C1D', borderRadius: 10, padding: '2px 10px', fontSize: 11, fontWeight: 600, display: 'inline-block' }}>部分已发 {pr.shipped}/{pr.total}</span>;
    }
    const p = ORDER_PILL[cls];
    return <span style={{ background: p.bg, color: p.color, borderRadius: 10, padding: '2px 10px', fontSize: 11, fontWeight: 600, display: 'inline-block' }}>{p.text}</span>;
  };
  // 商品种数：spec_code 逗号分隔计数（≥2 折叠显示 +N）
  const specCountOf = (o) => (o.spec_code ? String(o.spec_code).split(/[,，、]/).filter(s => s.trim()).length : 1);

  // ===== 客户列表（12 列表格，老板要求从卡片改回列表） =====
  const cooperateDaysOf = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr + 'T00:00:00');
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const days = Math.floor((today - d) / 86400000);
    return days >= 0 ? days : 0;
  };
  const rankedCustomerIds = [...(customers || [])].sort((a, b) => (Number(b.total_amount) || 0) - (Number(a.total_amount) || 0)).map(c => c.id);
  const rankOfCustomer = (r) => {
    const rank = rankedCustomerIds.indexOf(r.id) + 1;
    const bg = rank === 1 ? '#D85A30' : rank === 2 ? '#888780' : rank === 3 ? '#BA7517' : '#F1EFE8';
    const color = rank <= 3 ? '#fff' : '#5F5E5A';
    return <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: '50%', background: bg, color, fontSize: 12, fontWeight: 600 }}>{rank}</span>;
  };
  const customerTableColumns = [
    { title: '客户名称', dataIndex: 'name', width: 190, fixed: 'left', render: (v, r) => <a style={{ fontWeight: 600 }} onClick={() => setDrawerCust(r)}>{v}</a> },
    { title: '客户姓名', dataIndex: 'contact', width: 100, render: v => v || '-' },
    { title: '联系电话', dataIndex: 'phone', width: 130, render: v => v || '-' },
    { title: '客户地址', dataIndex: 'address', ellipsis: true, render: v => v || '-' },
    { title: '职位', dataIndex: 'position', width: 100, render: v => v || '-' },
    { title: '客户标签', dataIndex: 'tags', width: 160, render: tags => (Array.isArray(tags) && tags.length) ? tags.map(t => <Tag key={t} style={{ marginBottom: 2 }}>{t}</Tag>) : '-' },
    { title: '建联时间', dataIndex: 'first_order_date', width: 110, render: v => v || '-' },
    { title: '合作天数', dataIndex: 'first_order_date', width: 90, align: 'right', render: v => v ? `${cooperateDaysOf(v)} 天` : '-' },
    { title: '合作总次数', dataIndex: 'order_count', width: 100, align: 'right', render: v => `${v || 0} 单` },
    { title: '合作金额', dataIndex: 'total_amount', width: 110, align: 'right', render: v => <span style={{ color: 'var(--brand)', fontWeight: 600 }}>{fmtMoney(v)}</span> },
    { title: '合作排名', width: 90, align: 'center', render: (_, r) => rankOfCustomer(r) },
    { title: '客户分级', dataIndex: 'level', width: 90, align: 'center', render: v => <Tag color={LEVEL_COLOR[v] || 'default'}>{v || '普通'}</Tag> },
    { title: '操作', width: 90, fixed: 'right', align: 'center', render: (_, r) => r.name !== '待归属' ? (
      <Space size={0}>
        <Button size="small" type="link" onClick={() => { setEditCust(r); setCustModal(true); }}>编辑</Button>
        <Popconfirm title={`删除客户「${r.name}」？`} onConfirm={() => delCustomer(r)}><Button size="small" type="text" danger icon={<DeleteOutlined />} /></Popconfirm>
      </Space>
    ) : null },
  ];

  const recentOrderColumns = [
    { title: '订单号', dataIndex: 'platform_order_no', width: 170, fixed: 'left', render: (v, r) => {
      const st = shipStatusTag(r);
      const stColor = st.color === 'green' ? '#3F8600' : st.color === 'volcano' ? '#D85A30' : '#BA7517';
      return (
        <div>
          <a style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontWeight: 600, fontSize: 12 }} onClick={() => setOrderDetail(r)}>{v}</a>
          <div style={{ fontSize: 11, color: stColor }}>{st.text}</div>
        </div>
      );
    } },
    { title: '下单客户', dataIndex: 'customer_name', width: 140, ellipsis: true, render: v => <span style={{ fontWeight: 600 }}>{v}</span> },
    { title: '下单时间', width: 90, render: (_, r) => orderDateOf(r) || '-' },
    { title: '商品名称', dataIndex: 'product_name', ellipsis: true },
    { title: '商品编码', dataIndex: 'spec_code', width: 170, ellipsis: true, render: v => <span style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 11, color: '#2F54EB' }}>{v}</span> },
    { title: '件数', dataIndex: 'order_quantity', width: 70, align: 'right', render: v => fmtQty(v) },
    { title: '数量', dataIndex: 'product_quantity', width: 80, align: 'right', render: v => fmtQty(v) },
    { title: '运费', dataIndex: 'shipping_fee', width: 80, align: 'right', render: v => fmtMoney(v) },
    { title: '辅材', dataIndex: 'packaging_cost', width: 80, align: 'right', render: v => fmtMoney(v) },
    { title: '利润', dataIndex: 'profit', width: 95, align: 'right', render: v => <span style={{ color: (Number(v) || 0) < 0 ? '#CF1322' : '#3F8600', fontWeight: 600 }}>{(Number(v) || 0) > 0 ? '+' : ''}{fmtMoney(v)}</span> },
    { title: '备注', dataIndex: 'remark', ellipsis: true, render: v => v || '-' },
  ];

  const columns = [
    { title: '客户', dataIndex: 'name', width: 180, render: (v, r) => (
      <a style={{ fontWeight: 600 }} onClick={() => setDrawerCust(r)}>{v}</a>
    ) },
    { title: '等级', dataIndex: 'level', width: 80, render: v => <Tag color={LEVEL_COLOR[v] || 'default'}>{v}</Tag> },
    { title: '标签', dataIndex: 'tags', width: 160, render: tags => (tags || []).map(t => <Tag key={t} style={{ marginBottom: 2 }}>{t}</Tag>) },
    { title: '区域', dataIndex: 'region', width: 90, render: v => v || '-' },
    { title: '联系人', dataIndex: 'contact', width: 90, render: v => v || '-' },
    { title: '电话', dataIndex: 'phone', width: 130, render: v => v || '-' },
    { title: '成交额', dataIndex: 'total_amount', align: 'right', width: 100, render: v => <span style={{ color: 'var(--brand)', fontWeight: 600 }}>{fmtMoney(v)}</span> },
    { title: '利润', dataIndex: 'total_profit', align: 'right', width: 90, render: v => <span style={{ color: v < 0 ? '#CF1322' : '#3F8600' }}>{v > 0 ? '+' : ''}{fmtMoney(v)}</span> },
    { title: '利润得分', dataIndex: 'profit_score', align: 'center', width: 90, sorter: (a, b) => (a.profit_score || 0) - (b.profit_score || 0),
      render: v => {
        if (v == null) return <span style={{ color: '#bfbfbf' }}>-</span>;
        const c = v >= 60 ? 'gold' : v >= 30 ? 'blue' : 'default';
        return <Tag color={c} style={{ fontWeight: 700, marginRight: 0 }}>{v}</Tag>;
      } },
    { title: '应收', dataIndex: 'receivable', align: 'right', width: 100, render: v => <span style={{ color: v > 0 ? '#D85A30' : '#8c8c8c', fontWeight: 600 }}>{fmtMoney(v)}</span> },
    { title: '单数', dataIndex: 'order_count', align: 'center', width: 60, sorter: (a, b) => (a.order_count || 0) - (b.order_count || 0) },
    { title: '下单周期', dataIndex: 'cycle_days', align: 'center', width: 90, sorter: (a, b) => (a.cycle_days ?? 99999) - (b.cycle_days ?? 99999),
      render: v => v == null ? <span style={{ color: '#bfbfbf' }}>-</span> : <span style={{ fontWeight: 600 }}>{v} 天</span> },
    { title: '货量', dataIndex: 'total_quantity', align: 'right', width: 90, sorter: (a, b) => (a.total_quantity || 0) - (b.total_quantity || 0),
      render: v => <span>{fmtQty(v)} 件</span> },
    { title: '单均', dataIndex: 'avg_amount', align: 'right', width: 95, sorter: (a, b) => (a.avg_amount || 0) - (b.avg_amount || 0),
      render: v => <span style={{ color: '#26201C', fontWeight: 600 }}>¥{fmtMoney(v)}</span> },
    { title: '客户权重', dataIndex: 'weight', align: 'center', width: 100, sorter: (a, b) => (a.weight || 0) - (b.weight || 0),
      render: v => {
        if (v == null) return <span style={{ color: '#bfbfbf' }}>-</span>;
        const c = v >= 60 ? 'gold' : v >= 30 ? 'blue' : 'default';
        const t = v >= 60 ? '高' : v >= 30 ? '中' : '低';
        return <span><Tag color={c} style={{ fontWeight: 700, marginRight: 2 }}>{v}</Tag><span style={{ fontSize: 11, color: '#8c8c8c' }}>{t}</span></span>;
      } },
    { title: '跟进', dataIndex: 'pending_followup', align: 'center', width: 80, render: (v, r) => v > 0 ? <Tag color="red">{v} 待跟进</Tag> : <span style={{ color: '#bfbfbf' }}>-</span> },
    { title: '操作', align: 'right', width: 150, render: (_, r) => (
      <Space size={0}>
        <Button size="small" type="link" onClick={() => setDrawerCust(r)}>详情</Button>
        {r.name !== '待归属' && (
          <>
            <Button size="small" type="link" onClick={() => { setEditCust(r); setCustModal(true); }}>编辑</Button>
            <Popconfirm title={`删除客户「${r.name}」？`} onConfirm={() => delCustomer(r)}><Button size="small" type="text" danger icon={<DeleteOutlined />} /></Popconfirm>
          </>
        )}
      </Space>
    ) },
  ];

  return (
    <div className="page-container">
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 16, fontWeight: 600 }}>客户管理（线下 CRM）</div>
        <div style={{ color: '#8c8c8c', fontSize: 12, marginTop: 2 }}>
          管理线下成交客户（酒店/宾馆/经销商/批发），新建订单为待发货状态（预算利润），上传单号发货后进订单管理并扣库存、算利润，与商品成本表打通。
        </div>
      </div>

      {/* 看板（可点击联动：客户→客户Tab，成交/利润→订单Tab，应收→订单Tab+待回款筛选） */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={4}>
          <Card size="small" hoverable className="crm-kpi-card" onClick={() => setActiveTab('customers')}>
            <Statistic title="客户总数" value={stats.customer_count || 0} suffix="家" valueStyle={{ color: '#26201C' }} />
          </Card>
        </Col>
        <Col xs={12} md={5}>
          <Card size="small" hoverable className="crm-kpi-card" onClick={() => { setActiveTab('orders'); setOrderFilter('all'); setRecentPage(1); }}>
            <Statistic title="本月成交额" value={stats.month_amount || 0} precision={2} prefix="¥" valueStyle={{ color: 'var(--brand)' }} />
          </Card>
        </Col>
        <Col xs={12} md={5}>
          <Card size="small" hoverable className="crm-kpi-card" onClick={() => { setActiveTab('orders'); setOrderFilter('all'); setRecentPage(1); }}>
            <Statistic title="发货后利润" value={stats.month_profit || 0} precision={2} prefix="¥" valueStyle={{ color: (stats.month_profit || 0) >= 0 ? '#3F8600' : '#CF1322' }} />
          </Card>
        </Col>
        <Col xs={12} md={5}>
          <Card size="small" hoverable className="crm-kpi-card" onClick={() => { setActiveTab('orders'); setOrderFilter('all'); setRecentPage(1); }}>
            <Statistic title="本月预估利润" value={stats.month_estimate_profit || 0} precision={2} prefix="¥" valueStyle={{ color: (stats.month_estimate_profit || 0) >= 0 ? '#3F8600' : '#CF1322' }} />
          </Card>
        </Col>
        <Col xs={12} md={5}>
          <Card size="small" hoverable className="crm-kpi-card" onClick={() => { setActiveTab('orders'); setOrderFilter('unpaid'); setRecentPage(1); }}>
            <Statistic title="应收总额" value={stats.total_receivable || 0} precision={2} prefix="¥" valueStyle={{ color: (stats.total_receivable || 0) > 0 ? '#D85A30' : '#3F8600' }} />
          </Card>
        </Col>
      </Row>

      {/* 主内容：客户 / 订单 / TOP 客户 三个 Tab 分块 */}
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'customers',
            label: `客户${customers.length ? ` ${customers.length}` : ''}`,
            children: (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                  <Input.Search placeholder="搜客户名/联系人/电话/微信" allowClear style={{ width: 280 }}
                    onSearch={setKeyword} onChange={e => { if (!e.target.value) setKeyword(''); }} />
                  <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditCust(null); setCustModal(true); }}>新增客户</Button>
                </div>
                {customers.length === 0 ? (
                  <Empty description="暂无客户，点击右上角「新增客户」开始建档" style={{ margin: '48px 0' }} />
                ) : (
                  <Table
                    size="small"
                    rowKey="id"
                    columns={customerTableColumns}
                    dataSource={[...customers].sort((a, b) => (Number(b.total_amount) || 0) - (Number(a.total_amount) || 0))}
                    pagination={false}
                    scroll={{ x: 1500 }}
                    locale={{ emptyText: '暂无客户' }}
                  />
                )}
              </div>
            ),
          },
          {
            key: 'orders',
            label: `订单${recentTotal ? ` ${recentTotal}` : ''}`,
            children: (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                  <Space size={8} wrap>
                    {[
                      { k: 'all', label: '全部' },
                      { k: 'unshipped', label: '待发货' },
                      { k: 'partial', label: '部分已发' },
                      { k: 'unpaid', label: '待回款' },
                      { k: 'settled', label: '已发货' },
                    ].map(f => {
                      const active = orderFilter === f.k;
                      const n = orderCounts ? (orderCounts[f.k] || 0) : null;
                      return (
                        <span key={f.k} onClick={() => { setOrderFilter(f.k); setRecentPage(1); }}
                          style={{
                            cursor: 'pointer', padding: '4px 14px', borderRadius: 16, fontSize: 12, fontWeight: active ? 600 : 400,
                            background: active ? 'var(--brand)' : '#fff',
                            color: active ? '#fff' : (f.k === 'unshipped' || f.k === 'partial' || f.k === 'unpaid' ? '#993C1D' : '#5F5E5A'),
                            border: active ? '1px solid var(--brand)' : '1px solid #F0C9B5',
                          }}>
                          {f.label}{n != null ? ` ${n}` : ''}
                        </span>
                      );
                    })}
                  </Space>
                  <Button type="primary" icon={<PlusOutlined />} onClick={() => setQuickOrder(true)}>新建订单</Button>
                </div>
                <Table
                  size="small"
                  rowKey="platform_order_no"
                  columns={recentOrderColumns}
                  dataSource={recentOrders}
                  loading={recentLoading}
                  pagination={{
                    current: recentPage,
                    pageSize: 10,
                    total: recentTotal,
                    onChange: setRecentPage,
                    showTotal: t => `共 ${t} 单`,
                  }}
                  scroll={{ x: 1400 }}
                  locale={{ emptyText: '暂无线下成交订单，点击右上角「新建订单」开始' }}
                />
              </div>
            ),
          },
          {
            key: 'top',
            label: 'TOP 客户',
            children: (
              stats.top && stats.top.length > 0 ? (
                <Row gutter={[16, 16]}>
                  {stats.top.map((t, i) => (
                    <Col key={t.name} xs={24} sm={12} md={8} xl={6}>
                      <div className="crm-cust-card">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                          <span style={{ width: 28, height: 28, borderRadius: '50%', background: i < 3 ? '#D85A30' : '#E8D8CC', color: i < 3 ? '#fff' : '#8C8178', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>{i + 1}</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: 14, color: '#26201C', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.name}</div>
                            <div style={{ fontSize: 12, color: '#8c8c8c' }}>{t.order_count} 单</div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                          <span style={{ fontSize: 12, color: '#8c8c8c' }}>成交额</span>
                          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--brand)' }}>¥{fmtMoney(t.total_amount)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: 12, color: '#8c8c8c' }}>利润</span>
                          <span style={{ fontSize: 13, fontWeight: 600, color: t.total_profit >= 0 ? '#3F8600' : '#CF1322' }}>{t.total_profit >= 0 ? '+' : ''}{fmtMoney(t.total_profit)}</span>
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              ) : <Empty description="暂无成交数据" style={{ margin: '48px 0' }} />
            ),
          },
        ]}
      />

      {/* 弹窗 */}
      {custModal && <CustomerModal open={custModal} customer={editCust} onCancel={() => setCustModal(false)} onDone={() => { setCustModal(false); refresh(); }} />}
      {drawerCust && <CustomerDetailModal customer={drawerCust} open={!!drawerCust} onClose={() => setDrawerCust(null)} onChanged={refresh} prodOpts={prodOpts} allCustomers={customers} />}
      {quickOrder && <OrderModal open={quickOrder} customer={null} customers={customers} prodOpts={prodOpts} onCancel={() => setQuickOrder(false)} onDone={() => { setQuickOrder(false); refresh(); }} />}
      {orderDetail && <EditOrderModal open={!!orderDetail} order={orderDetail} prodOpts={prodOpts} allCustomers={customers} onCancel={() => setOrderDetail(null)} onDone={() => { setOrderDetail(null); loadRecentOrders(recentPage, orderFilter); loadStats(); }} onChanged={() => { loadRecentOrders(recentPage, orderFilter); loadStats(); }} onShip={(o) => setDetailShip(o)} />}
      {/* 详情弹窗内发起的分批发货：平级渲染（发货后拉最新订单更新详情弹窗，保持详情打开） */}
      {detailShip && <ShipModal open={!!detailShip} order={detailShip} zIndex={2000} onCancel={() => setDetailShip(null)}
        onDone={async () => {
          setDetailShip(null);
          try {
            const res = await apiFetch(`/api/orders/detail?platform_order_no=${encodeURIComponent(detailShip.platform_order_no)}`).then(r => r.json());
            if (res.success && res.data) setOrderDetail(res.data);
          } catch (e) { /* 静默 */ }
          loadRecentOrders(recentPage, orderFilter); loadStats();
        }} />}
    </div>
  );
}
